import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../hooks/useAuth.jsx";
import { contributesWithoutEditing } from "../lib/roles.js";
import { normalizeMediaUrl } from "../lib/mediaUrl.js";
import { coverGeometry, planKenBurnsCover } from "../lib/coverGeometry.js";
import { useAutoHide } from "../hooks/useAutoHide.js";
import { useImmersive } from "../hooks/useImmersive.js";
import StageControls from "../components/stage/StageControls.jsx";
import StageWords from "../components/stage/StageWords.jsx";
import { useIsCompact } from "../hooks/useIsCompact.js";
import { useIsPortrait } from "../hooks/useIsPortrait.js";
import { useIsPreviewTab } from "../hooks/useIsPreviewTab.js";
import ClientBreadcrumb from "../components/ClientBreadcrumb.jsx";
import ReflectionPrompt from "../components/ReflectionPrompt.jsx";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner.jsx";
import ErrorBanner from "../components/ErrorBanner.jsx";

// ── CSS keyframe injection ────────────────────────────────────────────────────
// Inject once at module load. Includes sceneFadeIn (v1.5+) and kenBurnsPan (v3).

if (typeof document !== "undefined" && !document.querySelector("#cg-player-keyframes")) {
  const s = document.createElement("style");
  s.id = "cg-player-keyframes";
  s.textContent = `
    @keyframes sceneFadeIn {
      from { opacity: 0 }
      to   { opacity: 1 }
    }
    @keyframes sceneFadeOut {
      from { opacity: 1 }
      to   { opacity: 0 }
    }
    /* The ending (Oct 17): the last frame holds, a veil of its own picture,
       blurred and darkened, rises over it, and the card comes up through. */
    @keyframes endVeilIn {
      from { opacity: 0 }
      to   { opacity: 1 }
    }
    @keyframes endRise {
      from { opacity: 0; transform: translateY(10px) }
      to   { opacity: 1; transform: none }
    }
    /* textFadeIn — whole-block fade for text_arrival:"fade_in" (§2.2) */
    @keyframes textFadeIn {
      from { opacity: 0 }
      to   { opacity: 1 }
    }
    /* kenBurnsPan_legacy — fixed pan, retained for migration window.
       Parameterized builds use per-scene injected keyframes (see injectKenBurnsKeyframe). */
    @keyframes kenBurnsPan_legacy {
      0%   { transform: scale(1.0); }
      100% { transform: scale(1.12); }
    }
  `;
  document.head.appendChild(s);
}

/**
 * Injects a @keyframes rule for a given (startScale, endScale) pair (idempotent).
 * Returns the keyframe name so the caller can reference it in an animation string.
 * Keyed on both values, not zoom_amount alone — transform-origin is set inline
 * on the element, separately.
 *
 * CG owns the source→letterbox→CSS mapping (§2.1):
 *   target {x,y} normalized on source → CSS transform-origin on the img element.
 *   scale() zooms toward the transform-origin point.
 *   This is exact when the image fills the container (square/near-container ratio)
 *   and directionally correct for letterboxed images — the same code path serves
 *   wave-one coarse targets and wave-two vision-analyzed face targets.
 *
 * startScale is the "fill scale" baseline (Issue 3 fix, see mapTargetToTransformOrigin):
 * the uniform scale that takes object-fit:contain sizing to object-fit:cover sizing,
 * eliminating any letterbox/pillarbox margin BEFORE the authored zoom is applied.
 * endScale = startScale * zoom_amount. Without this, a zoom starting at scale(1)
 * only fills the margin-axis gradually, producing visibly asymmetric cropping onset
 * between the two axes — width "growing" to catch up with an already-cropping height
 * (or vice versa) instead of cropping both axes together from the first frame.
 * fillScale defaults to 1 for non-fill-mode rendering (no measured container/letterbox
 * to correct for there), which reproduces the old scale(1)→scale(zoom_amount) behavior
 * unchanged.
 */
/**
 * A face this close to the frame's centre (per axis, in normalized frame
 * units) is treated as already centred: no pan, and no zoom either. A zoom
 * toward something that is already where the eye is going adds motion
 * without meaning. Tune here; 0.06 is six percent of the frame.
 */
export const KB_DEAD_ZONE = 0.06;

/**
 * Plan the Ken Burns move for one image — pure geometry, no DOM.
 *
 * Returns { still, zoom, tx, ty }: `still` means render the image with no
 * motion at all; otherwise animate scale(1)→translate(tx,ty) scale(zoom),
 * with tx/ty as fractions of the element's own size.
 *
 * WHY THE PAN IS CLAMPED AS A VECTOR, NOT PER AXIS
 *
 * The translate that would centre the face is (0.5 − t) × zoom on each axis.
 * But the image can only shift (zoom − 1)/2 before its edge leaves the clip —
 * at zoom 1.15 that is 0.075 of the frame, and a face at the rule-of-thirds
 * needs about 0.19. So both axes are nearly always clamped.
 *
 * Clamping each axis on its own destroyed the direction: a face 8% right and
 * 25% up needs (0.09, 0.29), which per-axis clamping turned into (0.075,
 * 0.075) — a 45° drift whatever the face's true bearing. The near axis got
 * corrected almost fully and the far one only a fraction, which read as the
 * pan "fixing left/right but not up/down". It was never about direction; it
 * was about which axis happened to be closer.
 *
 * Scaling both components by one factor keeps the trajectory pointing at the
 * face. Full centring is not the goal — the move stays as gentle as before —
 * but the move now aims where it should.
 *
 * hasTarget: whether PE supplied a target at all. Without one the image
 * keeps the gentle centre zoom it has always had; the dead zone applies only
 * to a real target that happens to sit near the middle.
 */
export function planKenBurns(zoomAmount, targetX, targetY, hasTarget = true) {
  const dx = 0.5 - targetX;
  const dy = 0.5 - targetY;

  if (hasTarget && Math.abs(dx) < KB_DEAD_ZONE && Math.abs(dy) < KB_DEAD_ZONE) {
    return { still: true, zoom: 1, tx: 0, ty: 0 };
  }
  if (zoomAmount <= 1) {
    return { still: true, zoom: 1, tx: 0, ty: 0 };
  }

  const maxPan  = (zoomAmount - 1) / 2;
  const wantX   = dx * zoomAmount;
  const wantY   = dy * zoomAmount;
  const largest = Math.max(Math.abs(wantX), Math.abs(wantY));
  const k       = largest > maxPan ? maxPan / largest : 1;

  return { still: false, zoom: zoomAmount, tx: wantX * k, ty: wantY * k };
}

function injectKenBurnsKeyframe(zoomAmount, targetX, targetY, hasTarget = true) {
  const plan = planKenBurns(zoomAmount, targetX, targetY, hasTarget);
  if (plan.still) return null;
  const zKey = Math.round(zoomAmount * 1000);
  const xKey = Math.round(targetX  * 10000);
  const yKey = Math.round(targetY  * 10000);
  const id   = `cg-kb-${zKey}-${xKey}-${yKey}`;
  const name = `kbZoom${zKey}_${xKey}_${yKey}`;
  if (typeof document !== "undefined" && !document.querySelector(`#${id}`)) {
    const txPct = (plan.tx * 100).toFixed(3);
    const tyPct = (plan.ty * 100).toFixed(3);
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `@keyframes ${name} { 0% { transform: scale(1.0); } 100% { transform: translate(${txPct}%, ${tyPct}%) scale(${plan.zoom}); } }`;
    document.head.appendChild(s);
  }
  return name;
}


// Cover variant (Plan G): the plan is per crop, so the keyframe is keyed on
// the rounded translate rather than the target.
function injectCoverKeyframe(plan) {
  if (!plan || plan.still) return null;
  const txPct = (plan.tx * 100).toFixed(3);
  const tyPct = (plan.ty * 100).toFixed(3);
  const name  = `kbCover${Math.round(plan.zoom * 1000)}_${txPct.replace(/[-.]/g, "_")}_${tyPct.replace(/[-.]/g, "_")}`;
  const id    = `cg-${name}`;
  if (typeof document !== "undefined" && !document.querySelector(`#${id}`)) {
    const st = document.createElement("style");
    st.id = id;
    st.textContent = `@keyframes ${name} { 0% { transform: scale(1.0); } 100% { transform: translate(${txPct}%, ${tyPct}%) scale(${plan.zoom}); } }`;
    document.head.appendChild(st);
  }
  return name;
}

/**
 * Source→letterbox geometry (§2.1).
 *
 * target {x,y} is normalized 0–1 on the ORIGINAL SOURCE image. This function
 * computes two things from the same letterbox geometry:
 *
 * (A) correctedOrigin {x, y} — for object-fit:contain rendering where the img
 *     element is larger than the letterboxed content rect. transform-origin is
 *     relative to the element's border box, not the content rect, so a flat
 *     target*100% lands on the wrong pixel whenever aspect ratios don't match.
 *     Retained for the non-fill fallback path and non-KB transitions.
 *
 * (B) letterboxRect {displayedW, displayedH, offsetX, offsetY} — the explicit
 *     dimensions and position of the image within the container, as if drawn by
 *     object-fit:contain. Used by the Ken Burns explicit-sizing path (see below)
 *     to size the <img> element to exactly the image content rect, eliminating
 *     letterbox margins entirely. When the element IS the image, transform-origin
 *     is just targetX*100% / targetY*100% — no offset correction needed.
 *
 * Pure function — no DOM access — directly unit-testable.
 */
function mapTargetToTransformOrigin(targetX, targetY, containerW, containerH, naturalW, naturalH) {
  if (!containerW || !containerH || !naturalW || !naturalH) {
    return { x: targetX * 100, y: targetY * 100,
             displayedW: null, displayedH: null, offsetX: 0, offsetY: 0 };
  }
  const containerAspect = containerW / containerH;
  const imageAspect     = naturalW / naturalH;

  let displayedW, displayedH;
  if (imageAspect > containerAspect) {
    displayedW = containerW;
    displayedH = containerW / imageAspect;
  } else {
    displayedH = containerH;
    displayedW = containerH * imageAspect;
  }
  const offsetX = (containerW - displayedW) / 2;
  const offsetY = (containerH - displayedH) / 2;

  // Corrected origin for object-fit:contain rendering (path A).
  const x = ((offsetX + targetX * displayedW) / containerW) * 100;
  const y = ((offsetY + targetY * displayedH) / containerH) * 100;

  return { x, y, displayedW, displayedH, offsetX, offsetY };
}

// ── CG-side locked template structure (§4.1) ──────────────────────────────────
// transition_type, transition_duration_ms, image_order_policy are CG constants.
// They are NOT emitted per-build; the spec emits template_id + params only.

const TEMPLATE_CONFIG = {
  crossfade_slideshow_v1: {
    transitionType:      "crossfade",
    transitionDurationMs: 400,
    imageOrderPolicy:    "as_given",
  },
  ken_burns_pan_v1: {
    transitionType:      "ken_burns",
    transitionDurationMs: 600,
    imageOrderPolicy:    "as_given",
  },
  single_image_static_v1: {
    transitionType:      "none",
    transitionDurationMs: 0,
    imageOrderPolicy:    "as_given",
  },
};

// ── Wave-one design system (§2/§3, field names working-target pending §0 fold) ─

/**
 * Safe fallbacks for every design field.
 * A missing or malformed scene.design always degrades to this.
 * Design is NEVER fail-loud — a design failure must never block a playthrough.
 */
const DESIGN_DEFAULTS = {
  transition:           "static_hold",
  transition_duration_ms: 400,
  dwell_ms:             null,     // null → fall back to layout.per_image_dwell_ms or duration_ms
  ken_burns:            null,
  mask_role:            "rounded_rect",
  text_arrival:         "instant",
  pre_line_pause_ms:    0,
  // design_mood: read-and-ignore (shapes PE's choices; no CG rendering effect wave one)
  // tts_instruction: CG pass-through; not read here
};

/**
 * Derive the effective design for a scene.
 * Priority: scene.design (v3 wave one) → layout-derived (migration window) → all defaults.
 * §4 migration: layout coexists with design during the window; design dispatch activates
 * once scene.design is present; layout-derived design is the fallback until then.
 *
 * NOTE: field names are working-target pending §0 PE Coder Tier-1-fold confirmation.
 */
function getDesign(scene) {
  try {
    if (!scene.design) {
      // Migration window: derive from scene.layout if present
      if (scene.layout) {
        const p = scene.layout.params || {};
        const tId = scene.layout.template_id || "static_hold";
        const dwell = p.per_image_dwell_ms ?? 3000;
        const order = p.image_index_order ?? [0];
        return {
          ...DESIGN_DEFAULTS,
          transition:           tId,
          dwell_ms:             order.map(() => dwell),  // uniform list
          loop:                 p.loop !== false,
          starting_index:       p.starting_index ?? 0,
          image_index_order:    order,
        };
      }
      return { ...DESIGN_DEFAULTS, dwell_ms: [scene.duration_ms || 5000] };
    }

    const d = scene.design;

    // dwell_ms: always a list per contract. Defensive: int → [int], missing → fallback.
    let dwell_ms = d.dwell_ms;
    if (typeof dwell_ms === "number") dwell_ms = [dwell_ms];  // guard: bare int
    if (!Array.isArray(dwell_ms) || dwell_ms.length === 0) {
      dwell_ms = scene.layout?.params?.per_image_dwell_ms
        ? [scene.layout.params.per_image_dwell_ms]
        : [scene.duration_ms || 5000];
    }

    // §4 dwell invariant (assertion, not a blocker):
    // sum(dwell_ms) ≈ layout.per_image_dwell_ms × imageCount (both from duration_ms source)
    // A future refactor that breaks the shared-source assumption will surface here.

    return {
      // Ken Burns re-enabled (PE fixed detection via AWS Rekognition, verified).
      // The real transition passes through; ken_burns_zoom now renders instead of
      // being forced to crossfade. PE resolves zoom_amount against min_zoom_frame,
      // so CG animates the authored zoom with no clamp math needed.
      transition:           d.transition ?? DESIGN_DEFAULTS.transition,
      transition_duration_ms: d.transition_duration_ms ?? DESIGN_DEFAULTS.transition_duration_ms,
      dwell_ms,
      ken_burns:            d.ken_burns             ?? null,
      mask_role:            d.mask_role             ?? DESIGN_DEFAULTS.mask_role,
      text_arrival:         d.text_arrival          ?? DESIGN_DEFAULTS.text_arrival,
      pre_line_pause_ms:    d.pre_line_pause_ms      ?? 0,
      // Migration-window compat fields (derived from layout when no scene.design):
      loop:                 scene.layout?.params?.loop !== false,
      starting_index:       scene.layout?.params?.starting_index ?? 0,
      image_index_order:    scene.layout?.params?.image_index_order ?? [0],
    };
  } catch (_) {
    // Fail-fixed: any error in design extraction → all defaults
    return { ...DESIGN_DEFAULTS, dwell_ms: [scene.duration_ms || 5000] };
  }
}

/**
 * Returns an animation style for text blocks.
 * text_arrival: "fade_in" → gentle 300ms opacity fade.
 * "instant" or any other value → no animation.
 * prefersReducedMotion always wins.
 */
function textArrivalStyle(textArrival, prefersReducedMotion) {
  if (prefersReducedMotion || textArrival !== "fade_in") return {};
  return { animation: "textFadeIn 300ms ease both" };
}

/**
 * Returns image style for person mask.
 * mask_role "circle": borderRadius 50% + objectFit cover.
 * Defensive guard: fall back to rounded_rect if image is clearly non-square
 * (aspect ratio outside 0.77–1.3). Belt-and-suspenders — PE role-gates circle
 * to person images, but CG must not circle-crop a landscape photo.
 */
function maskStyle(maskRole, imgWidth, imgHeight) {
  if (maskRole !== "circle") {
    return { borderRadius: "var(--radius-md)", objectFit: "contain" };
  }
  // Guard: only apply circle to near-square images
  if (imgWidth && imgHeight) {
    const ratio = imgWidth / imgHeight;
    if (ratio > 1.3 || ratio < 0.77) {
      return { borderRadius: "var(--radius-md)", objectFit: "contain" };  // non-square fallback
    }
  }
  return { borderRadius: "50%", objectFit: "cover" };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Humanized fallback headings, used by the scene title card when PE did not
// author a `title` for a scene. These are generic (not client-specific — they
// can't say "Her key people"), but they ensure a caregiver never sees a raw
// snake_case scene_id like "important_people" in large on-screen type.
// A real PE-authored `title` always takes precedence over these.
const SCENE_LABELS = {
  hero:             "Meet",
  distinctive:      "Unique",
  must_know_1:      "Good to know",
  must_know_2:      "Good to know",
  must_know_3:      "Good to know",
  important_people: "Key people",
  people:           "Key people",
  joy:              "What brings joy",
  goals:            "Goals",
  daily_routine:    "Daily routine",
  sign_off:         "Let's go...",
};

// Turn any unmapped scene_id into a readable heading as a last resort:
// "some_new_scene" -> "Some new scene". Never shows raw snake_case.
function humanizeSceneId(sceneId) {
  if (!sceneId) return "";
  const mapped = SCENE_LABELS[sceneId];
  if (mapped) return mapped;
  const words = String(sceneId).replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
}

// Set by a mounted player given the `preview` prop (the script editor's
// in-page preview), so the image call sites below need no prop-drilling and
// still resolve to the preview storage root when the URL carries no
// ?preview=true. One player is mounted at a time; cleared on unmount.
let previewOverride = null;
// Likewise the API base a mounted player reads from (Oct 20): the intro's
// /api/clients/:id/playthrough by default, a section's when the host passes
// playthroughBase. Set on mount, cleared on unmount, one player at a time.
let baseOverride = null;

function assetUrl(clientId, path, isPreviewMode) {
  if (!clientId || !path) return null;
  if (isPreviewMode === undefined && previewOverride !== null) isPreviewMode = previewOverride;
  // In preview mode, output files live under the preview storage root, not the
  // finalized output root. Audio already switches on this; images must too, or
  // a preview-only author shows broken images (the files exist only in preview).
  //
  // isPreviewMode may be passed explicitly; when omitted we derive it from the
  // URL (?preview=true), the same source the player's isPreviewMode uses. This
  // keeps the three image call sites from having to prop-drill the flag.
  const preview = isPreviewMode ?? (
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("preview") === "true"
  );
  const slot = preview ? "preview" : "output";
  return `${baseOverride || `/api/clients/${clientId}/playthrough`}/${slot}/${path}`;
}

// A source_url baked into a finalized spec is an absolute, SIGNED CG-media URL
// built at finalize time for Presenter Engine, which is session-less and needs
// a signed absolute URL to fetch assets. The browser is NOT session-less: it
// carries the viewer's session (or share-link), so it needs neither the
// signature (which EXPIRES) nor the baked host (which goes STALE when the app
// moves — Railway -> AWS, or the eventual DNS cutover). Both are exactly why old
// playthroughs show broken images.
//
// So reduce any CG-media URL to its relative, UNSIGNED path. The browser then
// resolves it against the current origin, and serve_media authorizes it through
// require_capability via the session or share-link (every viewer role holds
// CAP_VIEW; share-link access is re-checked per request and instantly
// revocable). PE's own signed URLs are produced at finalize and are untouched
// by this — this only changes what the browser does with the value at render.
//
// Only CG-media URLs (…/api/clients/<cid>/media/<mid>) are rewritten; anything
// else (e.g. playthrough-output asset paths from assetUrl) is returned
// unchanged. A ?v=thumb|display variant is preserved (it selects the rendition);
// the signature query is dropped.
function msToSec(ms) {
  return (ms / 1000).toFixed(1) + "s";
}

function resolveImages(scene, fallbackPlacements) {
  if (scene.images && scene.images.length > 0) return scene.images;
  if (scene.image) {
    // v3: scene.image is {path, alt, mime_type} — return it directly.
    // v1.5 back-compat: bare string — wrap it.
    if (typeof scene.image === "string") return [{ path: scene.image, alt: "" }];
    return [scene.image];
  }
  if (fallbackPlacements?.length > 0)
    return fallbackPlacements.map(p => ({ url: p.display_url, alt_text: p.alt_text || "" }));
  return [];
}

/** Normalize v2/v3 (list) and v1 (single object) audio shapes → first-segment path. */
function getAudioPath(scene) {
  if (!scene.audio) return null;
  if (Array.isArray(scene.audio)) return scene.audio[0]?.path ?? null;
  return scene.audio.path ?? null;    // v1 back-compat
}

function usePrefersReducedMotion() {
  const [prefers, setPrefers] = useState(
    () => typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = e => setPrefers(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return prefers;
}

// ── SceneImages — v3 layout dispatch ─────────────────────────────────────────

/**
 * v3 image renderer — wave-one design-aware.
 *
 * Accepts `design` (from getDesign) when present. Falls back to reading `layout`
 * directly during the migration window (§4). Both coexist; layout is the
 * safety net, design is the progressive layer.
 *
 * Per-scene parameterization (§2.1):
 *   - crossfade duration: design.transition_duration_ms (not hardcoded 400ms)
 *   - Ken Burns: design.ken_burns.zoom_amount + target → dynamic @keyframes +
 *     transform-origin (source→letterbox→CSS mapping, §2.1 seam)
 *   - dwell: design.dwell_ms list (per-image variable dwell via setTimeout chain)
 *
 * design_mood: read-and-ignore. tts_instruction: CG pass-through, not read here.
 * Fail-fixed: any error → static display, playthrough continues.
 */
/**
 * The moments (ms from scene start) at which each image after the first
 * takes over. Per-image dwell_ms from the design when it covers every image;
 * otherwise the scene's duration split evenly (or the design's first dwell
 * when no duration is known). Exported for tests.
 */
/**
 * The same moments from narration cues (contract §13, Oct 25), when every
 * image in the scene has one: each image comes on at its cue's start_ms, the
 * crossfade centred on that moment. Null — so the caller uses sceneDwellPlan —
 * when any image lacks a usable cue or the starts don't run forward in display
 * order. A missing or malformed cue never blocks playback (§13.5).
 */
export function sceneCuePlan(images, indexOrder, transitionMs = 0) {
  if (!Array.isArray(images) || !Array.isArray(indexOrder) || indexOrder.length < 2) return null;
  const starts = [];
  for (const i of indexOrder) {
    const st = images[i]?.cue?.start_ms;
    if (!Number.isFinite(st) || st < 0) return null;
    starts.push(st);
  }
  for (let k = 1; k < starts.length; k++) if (starts[k] < starts[k - 1]) return null;
  const half = Math.max(0, (transitionMs || 0) / 2);
  return starts.slice(1).map(st => Math.max(0, st - half));
}

export function sceneDwellPlan(design, count, durationMs, rebuilt) {
  const n = Math.max(1, count);
  const dwell = Array.isArray(design?.dwell_ms) ? design.dwell_ms.filter(d => Number.isFinite(d) && d > 0) : [];
  let per;
  if (!rebuilt && dwell.length >= n) per = dwell.slice(0, n);
  else if (durationMs > 0)          per = Array.from({ length: n }, () => durationMs / n);
  else                              per = Array.from({ length: n }, () => dwell[0] ?? 3000);
  const bounds = [];
  let t = 0;
  for (let i = 0; i < n - 1; i++) { t += per[i]; bounds.push(t); }
  return bounds;
}
export function positionAt(bounds, elapsedMs) {
  let pos = 0;
  for (const b of bounds) { if (elapsedMs >= b) pos += 1; else break; }
  return pos;
}

// ── Image readiness (Oct 12) ─────────────────────────────────────────────────
// A picture is never shown until it is fetched AND decoded, and the one on
// screen stays until the next has faded in over it. Before this, every change
// unmounted the <img> and mounted a new one with an unfetched URL: a blank
// frame, then the JPEG painting top to bottom, then the "fade". The cache is
// module-wide so a scene's images, fetched during the previous scene, are
// instant when their turn comes.
const imageCache = new Map();   // url → Promise<string|null>  (a blob: URL once decoded)
const imageReady = new Map();   // url → blob: URL, for synchronous lookup at render
export function preloadImage(url) {
  if (!url) return Promise.resolve(null);
  if (imageCache.has(url)) return imageCache.get(url);
  // The bytes are fetched once and kept as a blob: URL, so the <img> that
  // shows the picture never goes back to the network — with a plain URL the
  // browser's memory cache usually, but not always, spares the second fetch.
  const p = (async () => {
    try {
      if (typeof fetch === "undefined" || typeof Image === "undefined") return null;
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) return null;
      const blob = await res.blob();
      const src = URL.createObjectURL(blob);
      await new Promise((resolve) => {
        const im = new Image();
        im.onload = () => { if (typeof im.decode === "function") im.decode().catch(() => {}).then(resolve); else resolve(); };
        im.onerror = () => resolve();
        setTimeout(resolve, 8000);   // a stalled decode must not stall the show
        im.src = src;
      });
      imageReady.set(url, src);
      return src;
    } catch {
      return null;
    }
  })();
  imageCache.set(url, p);
  return p;
}
/** The decoded copy's URL when ready, else the original (shown progressively). */
export function readySrc(url) { return imageReady.get(url) || url; }
export function imageUrlOf(img, clientId) {
  if (!img) return null;
  return normalizeMediaUrl(img.source_url)
    || mediaIdUrl(img, clientId)
    || img.url
    || (img.path ? assetUrl(clientId, img.path) : null);
}
/**
 * A spec image by its library id (Oct 25). PE stopped writing source_url into
 * spec.json (the signed URLs it carried leaked, against §4.2) and now names
 * each image by `media_id` — the same id as in the bundle. Without this the
 * player fell back to `path`, i.e. a copy of the photo fetched out of PE's
 * output on every finalize.
 */
export function mediaIdUrl(img, clientId) {
  if (!img?.media_id || !clientId) return null;
  return `/api/clients/${clientId}/media/${encodeURIComponent(img.media_id)}?v=display`;
}
export function preloadSceneImages(scene, clientId) {
  (scene?.images || []).forEach(im => preloadImage(imageUrlOf(im, clientId)));
}

function SceneImagesV3Layout({ images, clientId, layout, design: designProp, sceneKey, fill, wrapHeight, prefersReduced, audioRef, animate, cover = false, durationMs = 0 }) {
  // ── Letterbox-mapping state (Issue 2 fix) ─────────────────────────────────
  // Only needed for the fill-mode path, where object-fit:contain can letterbox
  // the photo within the img's own border box (see mapTargetToTransformOrigin).
  const fillContainerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const [naturalSize,   setNaturalSize]   = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!fill || !fillContainerRef.current || typeof ResizeObserver === "undefined") return;
    const el = fillContainerRef.current;
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) setContainerSize({ w: entry.contentRect.width, h: entry.contentRect.height });
    });
    observer.observe(el);
    // Capture initial size immediately (ResizeObserver fires async on first observe too,
    // but this avoids a frame of stale/zero dimensions on mount).
    const rect = el.getBoundingClientRect();
    setContainerSize({ w: rect.width, h: rect.height });
    return () => observer.disconnect();
  }, [fill]);
  // Resolve design: prefer passed-in design (from scene-level getDesign call),
  // otherwise derive from layout for migration window compat.
  const design = designProp ?? (() => {
    const p = layout?.params ?? {};
    const tId = layout?.template_id ?? "static_hold";
    const dwell = p.per_image_dwell_ms ?? 3000;
    const order = p.image_index_order ?? [0];
    return {
      transition: tId,
      transition_duration_ms: DESIGN_DEFAULTS.transition_duration_ms,
      dwell_ms: order.map(() => dwell),
      ken_burns: null,
      loop: p.loop !== false,
      starting_index: p.starting_index ?? 0,
      image_index_order: order,
    };
  })();

  const designOrder = design.image_index_order ?? (layout?.params?.image_index_order ?? [0]);
  // The design's image_index_order is computed from the scene as authored. The
  // hero scene can acquire its images LATER (via a placement fetch), in which
  // case designOrder is a stale [0] and would pin the scene to its first image
  // forever. When the actual image list is longer than the design's order,
  // rebuild the order from the images we really have.
  const indexOrder = (images.length > designOrder.length)
    ? images.map((_, i) => i)
    : designOrder;
  const startPos   = Math.max(0, Math.min(design.starting_index ?? 0, indexOrder.length - 1));
  // NEVER loop. A scene advances through its images and holds the last one
  // until the next scene begins.
  //
  // EVIDENCE (console instrumentation, live spec): PE sends loop:true on most
  // scenes. With loop:true the cycler does `floor(elapsed/dwell) % imageCount`,
  // which wraps back to image 0 whenever the audio outlasts imageCount*dwell —
  // producing a visible flash of the FIRST image at the END of the scene.
  // Measured on the live playthrough: hero (9 imgs, 28224ms/3136 = 9, 9%9=0),
  // goals (6 imgs, 28200/4700 = 6, 6%6=0) and daily_routine (6 imgs,
  // 34440/5740 = 6, 6%6=0) all wrapped — exactly the three scenes reported.
  //
  // Product decision: looping is never wanted. We ignore design.loop rather than
  // defaulting it, because PE explicitly sets it true and a default would not
  // help. Clamping holds the final image, which is the intended behaviour.
  // (design.loop is still parsed in getDesign for contract fidelity; the
  // renderer simply does not honour it.)

  const [pos, setPos] = useState(startPos);
  // Reset to the scene's starting image whenever the SCENE ITSELF changes.
  //
  // BUG HISTORY: this used to depend on [layout.template_id, design.transition,
  // startPos]. Every scene PE emits has template_id="crossfade_slideshow_v1",
  // transition="crossfade" and starting_index=0 — so those deps are IDENTICAL
  // across scenes and the effect never re-fired. `pos` carried over from the
  // previous scene, and combined with the currentTime reset below produced a
  // flash of the wrong image at every scene boundary. sceneKey is the scene's
  // identity, so it actually changes when the scene does.
  useEffect(() => { setPos(startPos); }, [sceneKey, startPos]);

  const imageIdx = indexOrder[pos] ?? 0;

  // Reset naturalSize when the displayed image changes so stale dimensions
  // from the previous image do not produce a wrong transform-origin.
  useEffect(() => {
    setNaturalSize({ w: 0, h: 0 });
  }, [imageIdx]);

  // Audio-synced image cycling: pos derived from audioRef.current.currentTime
  // instead of a free-running setInterval. Pause/resume sync and stop-at-end
  // are free: currentTime does not advance while paused.
  // When the coordinator added images after Author, the design was written
  // for fewer images than there are: its dwell list and "static" transition
  // describe a scene that no longer exists. Then every image gets an equal
  // share of the scene (Oct 8 — a scene with three images showed only one).
  const rebuilt = images.length > designOrder.length;
  // Narration cues first (Oct 25): each photo on screen for the words that go
  // with it. Otherwise the design's dwell, or an even split, as before.
  const dwellPlan = sceneCuePlan(images, indexOrder,
                                 design.transition_duration_ms ?? DESIGN_DEFAULTS.transition_duration_ms)
    || sceneDwellPlan(design, indexOrder.length, durationMs, rebuilt);
  useEffect(() => {
    const isStatic  = !rebuilt && (design.transition === "static_hold" ||
                                   design.transition === "single_image_static_v1");
    const singleImg = indexOrder.length <= 1;
    if (isStatic || singleImg) return;
    let live = true;
    const id = setInterval(() => {
      const audio = audioRef?.current;
      const elapsedMs = (audio?.currentTime ?? 0) * 1000;
      // Clamp: advance through the images, then hold the last one. Never wrap.
      const nextPos = positionAt(dwellPlan, elapsedMs);
      // Only switch once the next picture is decoded; until then the current
      // one holds (a late image costs a beat of hold, never a blank frame).
      const nextUrl = imageUrlOf(images[indexOrder[nextPos] ?? 0], clientId);
      preloadImage(nextUrl).then(() => { if (live) setPos(p => (p === nextPos ? p : nextPos)); });
    }, 250);
    return () => { live = false; clearInterval(id); };
  }, [dwellPlan.join(","), indexOrder.length, design.transition, rebuilt, audioRef, sceneKey]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch and decode every picture of this scene as soon as it starts.
  useEffect(() => {
    images.forEach(im => preloadImage(imageUrlOf(im, clientId)));
  }, [sceneKey, images, clientId]);

  const current  = images[imageIdx];
  // Prefer source_url (CG media, never expires) over the PE-relative path.
  // Falls back to assetUrl only for any path without a CG source URL.
  const url = current ? imageUrlOf(current, clientId) : null;

  // The outgoing layer: when the url changes, the frame on screen is
  // snapshotted (its computed transform, so a mid-pan Ken Burns holds where
  // it was) and kept underneath while the incoming image fades in over it,
  // then dropped. curImgRef still points at the old <img> during this render.
  // (Hooks here, above the early returns below.)
  const curImgRef   = useRef(null);
  const shownRef    = useRef(null);      // what the last render put on screen
  const outgoingRef = useRef(null);      // {url, style, geo, transform, until}
  const [, tick]    = useState(0);
  const fadeMs      = design.transition_duration_ms ?? DESIGN_DEFAULTS.transition_duration_ms;
  if (fill && url && shownRef.current && shownRef.current.url !== url && !prefersReduced) {
    const el = curImgRef.current;
    let frozen = null;
    try { frozen = el ? window.getComputedStyle(el).transform : null; } catch { frozen = null; }
    outgoingRef.current = {
      ...shownRef.current,
      transform: frozen && frozen !== "none" ? frozen : undefined,
      until: Date.now() + fadeMs + 80,
    };
  }
  useEffect(() => {
    if (!outgoingRef.current) return undefined;
    const wait = Math.max(0, outgoingRef.current.until - Date.now());
    const t = setTimeout(() => { outgoingRef.current = null; tick(n => n + 1); }, wait);
    return () => clearTimeout(t);
  }, [url]);

  // First picture of a scene: mount it only once decoded, so it never paints
  // progressively. (Later switches are gated in the interval above.) The
  // scene's title beat covers the wait on an ordinary connection.
  const [readyUrl, setReadyUrl] = useState(null);
  useEffect(() => {
    let live = true;
    if (url) preloadImage(url).then(() => { if (live) setReadyUrl(url); });
    return () => { live = false; };
  }, [url]);

  if (!current) return null;
  if (!url) return null;
  if (fill && readyUrl !== url && !outgoingRef.current) return <div ref={fillContainerRef} style={{ flex: 1, minHeight: 0, minWidth: 0, width: "100%", position: "relative", overflow: "hidden" }} />;

  // ── Animation assembly (§2.1) ─────────────────────────────────────────────
  let animation      = "none";
  let transformOrigin = undefined;
  let kbExplicitGeo   = null;   // {displayedW,displayedH,offsetX,offsetY} for the KB inner-clip render

  // Defer Ken Burns animation until both sizes are known AND playback has started,
  // so the pre-Play image renders static and KB begins on Play.
  const animationReady = containerSize.w > 0 && naturalSize.w > 0 && animate;

  if (!prefersReduced) {
    const isKenBurns = design.transition === "ken_burns_pan_v1" ||
                       design.transition === "ken_burns_zoom";
    const isCrossfade = design.transition === "crossfade_slideshow_v1" ||
                        design.transition === "crossfade";
    const transitionMs = design.transition_duration_ms ?? DESIGN_DEFAULTS.transition_duration_ms;
    const thisDwell    = (() => {
      const starts = [0, ...dwellPlan];
      const end = pos + 1 < starts.length ? starts[pos + 1] : (durationMs || starts[pos] + (design.dwell_ms?.[0] ?? 3000));
      return Math.max(500, end - starts[pos]);
    })();

    if (isCrossfade) {
      animation = `sceneFadeIn ${transitionMs}ms ease both`;

    } else if (isKenBurns) {
      // Read ken_burns from the CURRENT IMAGE (per-image spec), not from scene design.
      const kb         = current.ken_burns ?? {};
      // Gentle documentary Ken Burns: cap zoom at 1.15 (a subtle drift, not a
      // dramatic push). PE may author higher; CG clamps to a tasteful ceiling.
      // Tune KB_MAX_ZOOM to adjust overall intensity.
      const KB_MAX_ZOOM = 1.15;
      const zoomAmount = Math.max(1.0, Math.min(kb.zoom_amount ?? 1.08, KB_MAX_ZOOM));
      const hasTarget  = Number.isFinite(kb.target?.x) && Number.isFinite(kb.target?.y);
      const targetX    = hasTarget ? kb.target.x : 0.5;
      const targetY    = hasTarget ? kb.target.y : 0.5;

      // Pan-toward-face: transform-origin at 50%/50%, pan handled by keyframe translate.
      transformOrigin = "50% 50%";

      // Cover (the stage, Plan G): the crop is the geometry; the pan is bounded
      // by the zoom's slack plus the crop's margin on that side.
      if (cover && fill && containerSize.w && containerSize.h && naturalSize.w && naturalSize.h) {
        const geo  = coverGeometry(containerSize.w, containerSize.h, naturalSize.w, naturalSize.h,
                                   hasTarget ? targetX : 0.5, hasTarget ? targetY : 0.4);
        const plan = planKenBurnsCover(zoomAmount, geo, hasTarget);
        kbExplicitGeo = { displayedW: geo.displayedW, displayedH: geo.displayedH, offsetX: geo.offsetX, offsetY: geo.offsetY };
        const coverName = injectCoverKeyframe(plan);
        animation = (animationReady && coverName)
          ? `${coverName} ${thisDwell}ms ease-in-out forwards, sceneFadeIn ${transitionMs}ms ease both`
          : `sceneFadeIn ${transitionMs}ms ease both`;
      } else
      // Inner-clip geometry: size the img to the exact letterbox content rect so the
      // scale animation clips to the image's own bounds (gray areas never fill in).
      if (fill && containerSize.w && containerSize.h && naturalSize.w && naturalSize.h) {
        const geo = mapTargetToTransformOrigin(targetX, targetY, containerSize.w, containerSize.h, naturalSize.w, naturalSize.h);
        if (geo.displayedW) {
          kbExplicitGeo = {
            displayedW: geo.displayedW,
            displayedH: geo.displayedH,
            offsetX:    geo.offsetX,
            offsetY:    geo.offsetY,
          };
        }
      }

      if (!(cover && kbExplicitGeo)) {
        // null when the face is already centred (KB_DEAD_ZONE): the image holds
        // still and only fades in, like a crossfade scene.
        const kbName = injectKenBurnsKeyframe(zoomAmount, targetX, targetY, hasTarget);
        if (animationReady && kbName) {
          animation = `${kbName} ${thisDwell}ms ease-in-out forwards, sceneFadeIn ${transitionMs}ms ease both`;
        } else {
          animation = `sceneFadeIn ${transitionMs}ms ease both`;
        }
      }
    }
  }

  const imgKey   = `${design.transition}-${pos}`;
  const kbT      = current.ken_burns?.target;
  const coverPos = `${Math.round((Number.isFinite(kbT?.x) ? kbT.x : 0.5) * 100)}% ${Math.round((Number.isFinite(kbT?.y) ? kbT.y : 0.4) * 100)}%`;
  const imgStyle = cover
    ? { objectFit: "cover", objectPosition: coverPos, animation, ...(transformOrigin ? { transformOrigin } : {}) }
    : { objectFit: "contain", borderRadius: 8, animation, ...(transformOrigin ? { transformOrigin } : {}) };

  if (fill) {
    // Ken Burns inner-clip path: when geometry is known, render the img inside a
    // clip div sized to the letterbox content rect (displayedW × displayedH at
    // offsetX/offsetY) with its own overflow:hidden. The scale animation is clipped
    // to the image's own bounds, so the gray letterbox areas are never touched by
    // the zoom (no "growing"/margin-fill). Falls back to the plain inset:0/contain
    // render when geometry isn't ready yet — the animation hasn't fired in that
    // case, so the fallback is never visible during the zoom.
    const imgEl = (
      <img key={imgKey} ref={curImgRef} src={readySrc(url)} alt={current.alt || current.alt_text || ""}
        onLoad={e => setNaturalSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", ...imgStyle }} />
    );
    // Remember this frame for the next switch (style without its animation,
    // which the snapshot replaces with the frozen transform).
    const { animation: _a, ...staticStyle } = imgStyle;   // eslint-disable-line no-unused-vars
    shownRef.current = { url, style: staticStyle, geo: kbExplicitGeo, alt: current.alt || current.alt_text || "" };
    const out = outgoingRef.current;
    const wrapGeo = g => g
      ? { position: "absolute", left: g.offsetX, top: g.offsetY, width: g.displayedW, height: g.displayedH, overflow: "hidden", borderRadius: cover ? 0 : 8 }
      : null;
    // Contain (desktop): a picture narrower than the stage sits on a soft,
    // darkened blow-up of itself rather than bare ground, so two pictures of
    // different shapes dissolve as whole frames — no hard edge where a wide
    // one used to be. Cover (phone) has no margins and needs none.
    const backdrop = src => cover ? null : (
      <img src={readySrc(src)} alt="" aria-hidden="true"
        style={{ position: "absolute", inset: "-6%", width: "112%", height: "112%", objectFit: "cover",
                 filter: "blur(28px) brightness(0.55) saturate(0.9)", pointerEvents: "none" }} />
    );
    // The whole outgoing layer — backdrop and picture — fades out as the
    // incoming one fades in, so its margins dissolve instead of dropping away.
    const outgoingLayer = out && out.url !== url ? (
      <div key={`out-${out.url}`} aria-hidden="true"
           style={{ position: "absolute", inset: 0, animation: `sceneFadeOut ${fadeMs}ms ease both` }}>
        {backdrop(out.url)}
        {(() => {
          const img = (
            <img src={readySrc(out.url)} alt=""
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", ...out.style,
                       animation: "none", ...(out.transform ? { transform: out.transform } : {}) }} />
          );
          return out.geo ? <div style={wrapGeo(out.geo)}>{img}</div> : img;
        })()}
      </div>
    ) : null;
    const incomingBackdrop = backdrop(url);
    return (
      // minWidth:0 alongside minHeight:0 — flex items default to min-width:auto
      // (and min-height:auto), letting a flex item grow to its content's intrinsic
      // size even with overflow:hidden. Without this, a row-direction flex context
      // lets the container WIDTH grow to fit the visually-scaled image while height
      // stays fixed — exactly the reported asymmetric-crop. Both axes must override.
      <div ref={fillContainerRef} style={{ flex: 1, minHeight: 0, minWidth: 0, width: "100%", position: "relative", overflow: "hidden" }}>
        {incomingBackdrop && (
          <div key={`bd-${url}`} style={{ position: "absolute", inset: 0, animation: prefersReduced ? "none" : `sceneFadeIn ${fadeMs}ms ease both` }}>
            {incomingBackdrop}
          </div>
        )}
        {outgoingLayer}
        {kbExplicitGeo
          ? (
              <div style={{
                position: "absolute",
                left:     kbExplicitGeo.offsetX,
                top:      kbExplicitGeo.offsetY,
                width:    kbExplicitGeo.displayedW,
                height:   kbExplicitGeo.displayedH,
                overflow: "hidden",
                borderRadius: cover ? 0 : 8,
              }}>
                {imgEl}
              </div>
            )
          : imgEl}
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: wrapHeight, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
      <img key={imgKey} src={url} alt={current.alt || current.alt_text || ""}
        style={{ display: "block", maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", ...imgStyle }} />
    </div>
  );
}

/**
 * v1.5 / v2 image renderer — original time-division cycling.
 * Unchanged from the pre-v3 implementation.
 */
function SceneImagesClassic({ scene, images, clientId, fill, wrapHeight, prefersReduced, cover = false }) {
  const durationMs = scene.duration_ms || 5000;
  const [idx, setIdx] = useState(0);

  useEffect(() => { setIdx(0); }, [scene.scene_id]);

  useEffect(() => {
    if (images.length <= 1) return;
    const intervalMs = Math.max(500, durationMs / images.length);
    // Advance through the images and STOP on the last one — a scene holds its
    // final image until the next scene begins. Wrapping back to image 0 mid-
    // scene reads as a glitch. (Matches the v3 renderer's non-loop behaviour.)
    // Guard the stop OUTSIDE the state updater: updaters must stay pure.
    if (idx >= images.length - 1) return;
    const id = setInterval(() => {
      setIdx(i => (i >= images.length - 1 ? i : i + 1));
    }, intervalMs);
    return () => clearInterval(id);
  }, [images.length, durationMs, scene.scene_id, idx]);

  if (images.length === 0) return null;
  const current = images[idx];
  // Prefer source_url (CG media, never expires) over the PE-relative path.
  // Falls back to assetUrl only for any path without a CG source URL.
  const url = imageUrlOf(current, clientId);
  if (!url) return null;

  const animation = prefersReduced ? "none" : "sceneFadeIn 300ms ease both";

  if (fill) {
    return (
      <div style={{ flex: 1, minHeight: 0, width: "100%", position: "relative" }}>
        <img key={`${scene.scene_id}-${idx}`} src={url} alt={current.alt_text || ""}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: cover ? "cover" : "contain", objectPosition: cover ? "50% 40%" : undefined, borderRadius: cover ? 0 : 8, animation }} />
        {!cover && current.caption && (
          <p style={{ position: "absolute", bottom: 8, left: 0, right: 0, textAlign: "center", fontSize: 13, color: "rgba(255,255,255,0.9)", background: "rgba(0,0,0,0.35)", padding: "3px 8px" }}>
            {current.caption}
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: wrapHeight, background: "transparent", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
      <img key={`${scene.scene_id}-${idx}`} src={url} alt={current.alt_text || ""}
        style={{ display: "block", maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain", borderRadius: 8, animation }} />
      {current.caption && <p style={sceneStyles.imageCaption}>{current.caption}</p>}
    </div>
  );
}

// ── Text cards (PE v3.7, Oct 27) ──────────────────────────────────────────────
// A scene can carry cards[] beside images[]: verbatim words from the clause
// being spoken, shown where no photo fits. Photos and cards share one timeline,
// ordered by cue.start_ms. Behind cards sits the scene's `ambient` photo,
// dimmed and blurred, never at full strength. Only scenes with cards come here;
// every other scene plays exactly as before.

/** One timeline of a card scene's photos and cards, in cue order. */
export function beatsOf(scene) {
  const photos = (scene?.images || []).map(img => ({ kind: "photo", img, start: img?.cue?.start_ms }));
  const cards  = (scene?.cards || []).filter(c => c && typeof c.text === "string")
    .map(card => ({ kind: "card", card, start: card?.cue?.start_ms }));
  const beats = [...photos, ...cards];
  if (beats.some(b => !Number.isFinite(b.start))) {
    // No cues (should not happen with cards): keep photos then cards, evenly.
    return beats.map((b, i) => ({ ...b, start: null, order: i }));
  }
  return beats.sort((a, b) => a.start - b.start);
}

/** Which beat is on screen at `ms`: the last one whose cue (less half the
 *  crossfade, so the fade is centred on it) has passed; the first before that. */
export function activeBeatAt(beats, ms, fadeMs = 0, durationMs = 0) {
  if (!beats.length) return -1;
  if (beats[0].start == null) {
    const per = durationMs > 0 ? durationMs / beats.length : 3000;
    return Math.min(beats.length - 1, Math.max(0, Math.floor(ms / per)));
  }
  const half = Math.max(0, fadeMs / 2);
  let at = 0;
  for (let i = 1; i < beats.length; i++) if (ms >= beats[i].start - half) at = i;
  return at;
}

export function SceneBeats({ scene, clientId, design, audioRef, prefersReduced, stage = false }) {
  const beats  = beatsOf(scene);
  const fadeMs = prefersReduced ? 0 : (design?.transition_duration_ms ?? DESIGN_DEFAULTS.transition_duration_ms);
  const [at, setAt] = useState(0);
  useEffect(() => { setAt(0); }, [scene.scene_id]);
  useEffect(() => {
    beats.forEach(b => { if (b.kind === "photo") preloadImage(imageUrlOf(b.img, clientId)); });
    if (scene.ambient?.media_id) preloadImage(mediaIdUrl(scene.ambient, clientId));
    const id = setInterval(() => {
      const ms = (audioRef?.current?.currentTime ?? 0) * 1000;
      const next = activeBeatAt(beats, ms, fadeMs, scene.duration_ms || 0);
      setAt(a => (a === next ? a : next));
    }, 200);
    return () => clearInterval(id);
  }, [scene.scene_id, beats.length, fadeMs, audioRef, clientId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const current   = beats[at];
  const onCard    = current?.kind === "card";
  const ambientUrl = scene.ambient?.media_id ? mediaIdUrl(scene.ambient, clientId) : null;
  const fade = prefersReduced ? "none" : `opacity ${fadeMs}ms ease`;
  const layer = { position: "absolute", inset: 0, transition: fade };

  return (
    <div style={{ ...beatStyles.stage, ...(stage ? beatStyles.stageFull : {}) }}>
      {/* The ambient photo: dimmed, blurred, still; only under cards. */}
      <div aria-hidden="true" style={{ ...layer, opacity: onCard ? 1 : 0 }}>
        {ambientUrl && <img src={readySrc(ambientUrl)} alt="" style={beatStyles.ambient} />}
        <div style={beatStyles.wash} />
      </div>
      {beats.map((b, i) => (
        <div key={i} style={{ ...layer, opacity: i === at ? 1 : 0 }} aria-hidden={i !== at}>
          {b.kind === "photo" ? (
            <img src={readySrc(imageUrlOf(b.img, clientId))} alt={b.img?.alt_text || ""} style={beatStyles.photo} />
          ) : (
            <div style={beatStyles.cardWrap}>
              <p style={beatStyles.cardText}>{b.card.text}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const beatStyles = {
  stage:     { position: "relative", flex: 1, minHeight: 240, width: "100%", overflow: "hidden", background: "var(--color-bg-inset, #1a1915)", borderRadius: "var(--radius-md)" },
  stageFull: { borderRadius: 0, minHeight: 0 },
  photo:     { width: "100%", height: "100%", objectFit: "contain", display: "block" },
  ambient:   { width: "100%", height: "100%", objectFit: "cover", display: "block", opacity: 0.3, filter: "blur(8px)", transform: "scale(1.05)" },
  wash:      { position: "absolute", inset: 0, background: "rgba(20, 19, 16, 0.55)" },
  cardWrap:  { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "8%" },
  cardText:  { margin: 0, maxWidth: "22ch", textAlign: "center", color: "#fdfbf7", fontFamily: "var(--font-serif)", fontSize: "clamp(1.5rem, 4.5vw, 3.25rem)", lineHeight: 1.25, textShadow: "0 1px 12px rgba(0,0,0,0.45)" },
};

/** Cards carry these scenes; the scene's caption would put the words on screen twice. */
export function hidesOnScreenText(scene) {
  return scene?.treatment === "text_led" || scene?.treatment === "text_only";
}

/**
 * SceneImages — public component.
 * Dispatches to V3Layout (scene.design or scene.layout present) or Classic (v1.5/v2).
 * Accepts optional `design` prop from a parent that has already called getDesign(scene).
 */
function SceneImages({ scene, clientId, wrapHeight, fallbackImages, fill, design, audioRef, animate, cover = false }) {
  const images         = resolveImages(scene, fallbackImages);
  const prefersReduced = usePrefersReducedMotion();

  // v3.7: a scene with cards plays photos and cards on one timeline.
  if (Array.isArray(scene.cards) && scene.cards.length > 0) {
    return <SceneBeats scene={scene} clientId={clientId} design={design ?? getDesign(scene)}
      audioRef={audioRef} prefersReduced={prefersReduced} stage={cover} />;
  }

  if (images.length === 0) return null;

  // Dispatch to V3Layout when scene has design or layout block.
  if (scene.design || scene.layout) {
    const resolvedDesign = design ?? getDesign(scene);
    return <SceneImagesV3Layout
      images={images}
      clientId={clientId}
      layout={scene.layout}
      design={resolvedDesign}
      sceneKey={scene.scene_id}
      fill={fill}
      wrapHeight={wrapHeight}
      prefersReduced={prefersReduced}
      audioRef={audioRef}
      animate={animate}
      cover={cover}
      durationMs={scene.duration_ms || 0}
    />;
  }

  return <SceneImagesClassic
    scene={scene}
    images={images}
    clientId={clientId}
    fill={fill}
    wrapHeight={wrapHeight}
    prefersReduced={prefersReduced}
    cover={cover}
  />;
}

// ── Scene components ──────────────────────────────────────────────────────────

function HeroScene({ scene, preview, heroPhotoUrl, hasStarted, clientId, design, audioRef, stage = false, cover = false }) {
  const [sceneImages, setSceneImages] = useState([]);

  useEffect(() => {
    if (!hasStarted || sceneImages.length > 0 || !clientId || scene.images?.length > 0) return;
    fetch(`/api/clients/${clientId}/placements?type=scene_image&target=hero`)
      .then(r => r.json())
      .then(d => {
        if (Array.isArray(d) && d.length > 0)
          setSceneImages(d.map(p => ({ url: p.display_url, alt_text: p.alt_text || "" })));
      })
      .catch(() => {});
  }, [hasStarted, clientId, scene.images?.length]);

  // V3: use spec images directly (have per-image ken_burns); v1/v2: placement fetch.
  const heroSceneWithImages = scene.images?.length > 0 ? scene : { ...scene, images: sceneImages };
  const hasSceneImages = scene.images?.length > 0 || sceneImages.length > 0 || !!scene.image;
  const showSplash = !hasStarted && heroPhotoUrl;

  // v3: on_screen_text is the authored hero display text
  const displayText = scene.on_screen_text || preview?.display_name;

  const splashFit = cover ? { objectFit: "cover", objectPosition: "50% 35%" } : { objectFit: "contain", borderRadius: stage ? 0 : 8 };
  // Contain on the stage: same blurred self-backdrop the scenes use, so the
  // poster and the first scene are one frame rather than a jump.
  const splashBackdrop = (!cover && stage) ? (
    <img src={heroPhotoUrl} alt="" aria-hidden="true"
      style={{ position: "absolute", inset: "-6%", width: "112%", height: "112%", objectFit: "cover",
               filter: "blur(28px) brightness(0.55) saturate(0.9)", pointerEvents: "none" }} />
  ) : null;

  return (
    <div style={{ ...sceneStyles.wrap, ...(stage ? sceneStyles.wrapStage : {}) }}>
      {showSplash ? (
        <div style={{ flex: 1, minHeight: 0, width: "100%", position: "relative", overflow: "hidden" }}>
          {splashBackdrop}
          <img src={heroPhotoUrl} alt={displayText || ""}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", ...splashFit }} />
        </div>
      ) : hasSceneImages ? (
        <SceneImages scene={heroSceneWithImages} clientId={clientId} fill design={design} audioRef={audioRef} animate={hasStarted} cover={cover} />
      ) : heroPhotoUrl ? (
        <div style={{ flex: 1, minHeight: 0, width: "100%", position: "relative", overflow: "hidden" }}>
          {splashBackdrop}
          <img src={heroPhotoUrl} alt={displayText || ""}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", ...splashFit }} />
        </div>
      ) : null}

      {!stage && <div style={{ ...sceneStyles.previewBlock, flexShrink: 0, ...textArrivalStyle(design?.text_arrival, false) }}>
        {/* v3: display text from on_screen_text. v1/v2: from preview. text_arrival applied §2.2. */}
        {scene.on_screen_text ? (
          <>
            <p style={sceneStyles.heroTitle}>{scene.on_screen_text}</p>
            {preview?.pronouns && <p style={sceneStyles.pronouns}>{preview.pronouns}</p>}
          </>
        ) : preview ? (
          <>
            <p style={sceneStyles.heroTitle}>
              {preview.display_name}
              {preview.pronouns && <span style={sceneStyles.pronouns}> · {preview.pronouns}</span>}
            </p>
            {preview.hero_oneliner && <p style={sceneStyles.heroOneliner}>{preview.hero_oneliner}</p>}
          </>
        ) : null /* no caption — the scene heading is the title card */}
      </div>}
    </div>
  );
}

function StandardScene({ scene, clientId, preview, design, audioRef, animate, stage = false, cover = false }) {
  // [3] Two-field text model:
  // on_screen_text → display (v3 authored); absent → fall through to preview then title.
  // narration_text → TTS only; never displayed.
  // Both absent → valid visual-only scene; no error, no placeholder.
  const displayText = scene.on_screen_text
    ?? (preview?.title
      ? { title: preview.title, body: preview.description || preview.body }
      : null);

  if (stage) {
    return (
      <div style={{ ...sceneStyles.wrap, ...sceneStyles.wrapStage }}>
        <SceneImages scene={scene} clientId={clientId} fill design={design} audioRef={audioRef} animate={animate} cover={cover} />
      </div>
    );
  }

  return (
    <div style={sceneStyles.wrap}>
      <SceneImages scene={scene} clientId={clientId} fill design={design} audioRef={audioRef} animate={animate} />

      {scene.on_screen_text && !hidesOnScreenText(scene) ? (
        // v3: authored on-screen caption. scene.title is NOT rendered here — it
        // now appears as the full-bleed title card at the start of the scene.
        <div style={{ ...sceneStyles.previewBlock, flexShrink: 0, ...textArrivalStyle(design?.text_arrival, false) }}>
          <p style={sceneStyles.previewBody}>{scene.on_screen_text}</p>
        </div>
      ) : preview ? (
        // v1/v2: preview from CG script endpoint. No caption head — the scene
        // heading is the title card.
        <div style={{ ...sceneStyles.previewBlock, flexShrink: 0, ...textArrivalStyle(design?.text_arrival, false) }}>
          {preview.description && <p style={sceneStyles.previewBody}>{preview.description}</p>}
          {preview.body && <p style={sceneStyles.previewBody}>{preview.body}</p>}
        </div>
      ) : null /* no caption — valid silent/visual-only scene; heading is the title card */}
    </div>
  );
}

function PeopleScene({ scene, clientId, design, audioRef, animate }) {
  const sceneImages = resolveImages(scene);
  return (
    <div style={sceneStyles.wrap}>
      {(sceneImages.length > 0 || (scene.cards || []).length > 0) && <SceneImages scene={scene} clientId={clientId} fill design={design} audioRef={audioRef} animate={animate} />}
      {scene.people_visual && scene.people_visual.length > 0 && (
        <div style={{ ...sceneStyles.peopleRow, flexShrink: 0 }}>
          {scene.people_visual.map((person, i) => {
            const photoPath = person.image_path || (person.image && person.image.path) || person.photo_path;
            return (
              <div key={i} style={sceneStyles.personCard}>
                {photoPath ? (
                  // source_url is the CG media URL; photoPath is PE-relative (fallback)
                  <img src={normalizeMediaUrl(person.source_url) || assetUrl(clientId, photoPath)} alt={person.name}
                    style={{ ...sceneStyles.personPhoto, ...maskStyle(design?.mask_role) }} />
                ) : (
                  <div style={sceneStyles.personAvatar}>{person.name.charAt(0)}</div>
                )}
                <span style={sceneStyles.personName}>{person.name}</span>
                <span style={sceneStyles.personRel}>{person.relationship}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SignOffScene({ scene, clientId, preview, fallbackImages, design, audioRef, animate, stage = false, cover = false }) {
  // [3] Two-field text: on_screen_text for display; narration_text is TTS only.
  // Both absent → visual-only scene; show no text (valid per §5.1).
  const displayText = scene.on_screen_text ?? preview?.text ?? null;

  return (
    <div style={{ ...sceneStyles.wrap, justifyContent: "center", ...(stage ? sceneStyles.wrapStage : {}) }}>
      <SceneImages scene={scene} clientId={clientId} fill fallbackImages={fallbackImages} design={design} audioRef={audioRef} animate={animate} cover={cover} />
      {!stage && displayText && (
        <p style={{ ...sceneStyles.signOffText, flexShrink: 0, ...textArrivalStyle(design?.text_arrival, false) }}>{displayText}</p>
      )}
    </div>
  );
}

function SceneContent({ scene, clientId, preview, scenePlacementMap, heroPhotoUrl, hasStarted, audioRef, stage = false, cover = false }) {
  const fallback = scenePlacementMap?.[scene.scene_id];
  // Derive design once at the scene level; pass to all child components.
  // §3 working-target field names (field-locked on §0 PE Tier-1-fold confirmation).
  // Fail-fixed: getDesign always returns a safe design, never throws.
  const design = getDesign(scene);

  if (scene.scene_id === "hero")             return <HeroScene scene={scene} preview={preview} heroPhotoUrl={heroPhotoUrl} hasStarted={hasStarted} clientId={clientId} design={design} audioRef={audioRef} stage={stage} cover={cover} />;
  // The people scene plays its authored scene images like every other scene
  // (Oct 6): one authoring surface, the script page. The person photos in
  // people_visual are not used on the stage.
  if (scene.scene_id === "important_people") return stage
    ? <StandardScene scene={scene} clientId={clientId} preview={preview} design={design} audioRef={audioRef} animate={hasStarted} stage cover={cover} />
    : <PeopleScene scene={scene} clientId={clientId} preview={preview} design={design} audioRef={audioRef} animate={hasStarted} />;
  if (scene.scene_id === "sign_off")         return <SignOffScene scene={scene} clientId={clientId} preview={preview} fallbackImages={fallback} design={design} audioRef={audioRef} animate={hasStarted} stage={stage} cover={cover} />;
  return <StandardScene scene={scene} clientId={clientId} preview={preview} design={design} audioRef={audioRef} animate={hasStarted} stage={stage} cover={cover} />;
}

// ── Scene title card ──────────────────────────────────────────────────────────
// Shown full-bleed over the image area for the first SCENE_TITLE_MS of every
// scene, creating a clear demarcation between scenes. Text is scene.title —
// coordinator-editable on the play-through script page. Falls back to the raw
// scene_id when no title is authored (a visible nudge that it needs one).
//
// The card appears INSTANTLY (no fade-in) and fades out at the end. Fading in
// would let the scene's first image flash through underneath while the card is
// still translucent — the card must be opaque from frame one.

function SceneTitleCard({ scene, leaving, fadeMs }) {
  // Prefer PE's authored title; fall back to a humanized label (never the raw
  // snake_case scene_id).
  const heading = (scene?.title || "").trim() || humanizeSceneId(scene?.scene_id);
  if (!heading) return null;
  return (
    <div style={{
      ...titleCardStyles.wrap,
      opacity: leaving ? 0 : 1,
      transition: `opacity ${fadeMs}ms ease-out`,
    }}>
      <h2 style={titleCardStyles.heading}>{heading}</h2>
    </div>
  );
}

const titleCardStyles = {
  wrap: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    // Gentle warm ground — neither white nor black. Uses the app's existing
    // warm neutral so the card feels of a piece with the rest of the UI.
    background: "var(--color-bg-subtle)",
    padding: "var(--space-8)",
    zIndex: 5,
  },
  heading: {
    // Serif reads friendlier and less "UI" than the Inter body stack, and marks
    // the card as content rather than chrome. NOTE: --font-serif names Lora but
    // Lora is not currently loaded, so this renders as Georgia — a warm,
    // universally available fallback. Loading Lora would upgrade it for free.
    fontFamily: "var(--font-serif)",
    fontSize: "clamp(30px, 5vw, 58px)",
    // Regular weight — large enough to carry without shouting.
    fontWeight: "var(--weight-regular)",
    // Softer than --color-text-primary (#1a1915), which is near-black and harsh
    // at this size. Secondary is a warm grey: readable, not severe.
    color: "var(--color-text-secondary)",
    textAlign: "center",
    lineHeight: 1.25,
    margin: 0,
    letterSpacing: "-0.005em",
  },
};

// ── Composition warnings banner ───────────────────────────────────────────────
// Surfaces policy: "fixed" warnings only (§6.2 — "fixed = a default was substituted").
// soft warnings can be silent; the play-through works regardless.

function WarningsBanner({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  const fixed = warnings.filter(w => w.policy === "fixed");
  if (fixed.length === 0) return null;

  const summary = fixed.length === 1
    ? fixed[0].detail
    : `${fixed.length} scenes used default fallbacks during composition.`;

  return (
    <div style={warnStyles.banner}>
      <span style={warnStyles.icon}>⚠</span>
      <p style={warnStyles.text}>{summary}</p>
    </div>
  );
}

const warnStyles = {
  banner: { display: "flex", alignItems: "flex-start", gap: "var(--space-2)", background: "#fef9e7", border: "1px solid #f0c040", borderRadius: "var(--radius-md)", padding: "var(--space-3) var(--space-4)", flexShrink: 0 },
  icon:   { fontSize: "var(--text-sm)", color: "#b87800", flexShrink: 0 },
  text:   { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", margin: 0, lineHeight: "var(--leading-normal)" },
};

// ── Timeline ──────────────────────────────────────────────────────────────────

function Timeline({ scenes, currentIdx, totalMs, onSeek }) {
  return (
    <div style={timelineStyles.wrap}>
      {scenes.map((scene, i) => {
        const widthPct = totalMs > 0 ? (scene.duration_ms / totalMs) * 100 : 100 / scenes.length;
        return (
          <div
            key={scene.scene_id}
            style={{
              ...timelineStyles.segment,
              width: `${widthPct}%`,
              background: i === currentIdx ? "var(--color-accent)"
                : i < currentIdx ? "var(--color-accent-subtle)"
                : "var(--color-bg-inset)",
            }}
            onClick={() => onSeek(i)}
            title={scene.title || humanizeSceneId(scene.scene_id)}
          />
        );
      })}
    </div>
  );
}

// ── Main player ───────────────────────────────────────────────────────────────

/**
 * StreamEndCard — the player's final state, not an interruption.
 *
 * This replaces a modal that fired over a dimmed backdrop at onAudioEnded.
 * That modal made reflection the price of finishing: a DSP had just met
 * someone, and the app immediately asked them to evaluate the experience.
 * Meeting someone should end by arriving somewhere, not by being handed a
 * survey while the last image is still on screen.
 *
 * So the card IS where the stream lands, rendered in the scene area rather
 * than over it. The forward action is primary; Impressions sits beneath in
 * smaller type — an option rather than an expectation. Nothing is dismissed,
 * because nothing interrupted.
 *
 * ON THE SECOND BRANCH, WHICH IS DELIBERATELY ABSENT
 *
 * The spec paired "Continue" with a "Done" variant for a viewer who cannot
 * reach the profile's sections. That viewer does not exist yet: every audience
 * that can watch the stream can also read the profile — share-link visitors
 * included, which test_share_links.py pins by asserting a visitor gets 200 on
 * /profile. Building the branch now would be building a path nothing can walk.
 *
 * `forward` is therefore a single prop. The Done variant arrives with Part 2's
 * "links reach the introduction only", which is the change that actually
 * creates that viewer, and it plugs in here without touching anything else.
 */
// Exported for tests. The player itself needs audio, scenes, placements and
// four fetches to mount; the end card needs none of that, and testing it
// through the player would mean testing the player.
/** "her" / "him" / "them" from a pronouns string like "she/her". Unknown → null. */
export function objectPronoun(pronouns) {
  const p = (pronouns || "").toLowerCase();
  if (p.startsWith("she")) return "her";
  if (p.startsWith("he"))  return "him";
  if (p.startsWith("they")) return "them";
  return null;
}

/**
 * Who may be asked "one thing you'll remember" at the end: a signed-in user,
 * or a share-link visitor whose link asks for it (Oct 15). Preview, and links
 * made without the question, get null — which hides the prompt.
 */
export function reflectionClientId({ isPreviewMode, user, share, clientId }) {
  if (isPreviewMode || !clientId) return null;
  if (user) return clientId;
  if (share && share.asks_reflection !== false) return clientId;
  return null;
}

/**
 * The ending's choreography (Oct 17). From the moment the last clip ends:
 * the last frame holds; a veil of that frame, blurred and darkened, fades
 * up over it; the card rises through; its title first, then the question,
 * then the way on. Nothing arrives in the same instant. Reduced motion:
 * everything at once, a short fade.
 */
export function endTiming(reduced) {
  if (reduced) {
    const quick = "endVeilIn 200ms ease both";
    return { veil: quick, card: quick, title: "none", question: "none", actions: "none" };
  }
  return {
    veil:     "endVeilIn 1500ms ease 1650ms both",  // hold 1.65 s, then 1.5 s veil (Oct 18: +0.75 s)
    card:     "endRise 600ms ease 2450ms both",
    title:    "none",                               // arrives with the card
    question: "endRise 500ms ease 3350ms both",
    actions:  "endRise 500ms ease 3850ms both",
  };
}

export function StreamEndCard({ clientName, pronouns = "", forward, onReplay, onImpressions, clientId = null, afterRef = "full", reducedMotion = false }) {
  // With a name: "That's a bit about Maris." — after one scene and after the
  // whole introduction alike (David, Oct 18). Without one — for any reason —
  // "Thank you." beats a guess like "this person".
  const title = !clientName ? "Thank you." : `That's a bit about ${clientName}.`;
  const obj = objectPronoun(pronouns);
  const timing = endTiming(reducedMotion);
  // The question is an invitation, not a gate (Oct 17): the way on is always
  // there beneath it, and leaving without answering is simply not answering.
  // clientId is null in preview, and on share links that don't ask, which
  // hides the prompt.
  const [answered, setAnswered] = useState(false);
  return (
    <div style={ec.wrap}>
      <h2 style={{ ...ec.title, animation: timing.title }}>{title}</h2>

      {clientId && (
        <div style={{ width: "100%", display: "flex", justifyContent: "center", animation: timing.question }}>
          <ReflectionPrompt clientId={clientId} clientName={clientName} objectPronoun={obj} afterRef={afterRef}
                            onDone={() => setAnswered(true)} />
        </div>
      )}

      <div style={{ ...ec.actions, animation: timing.actions }}>
        <button type="button" style={ec.secondary} onClick={onReplay}>
          Play again
        </button>
        <button type="button" style={ec.primary} onClick={forward.onClick}>
          {forward.label}
        </button>
      </div>

      {/* Quiet by construction. Present for anyone who wants it, silent for
          anyone who doesn't, and never standing between the DSP and the
          profile they came here to read. */}
      {onImpressions && (clientId ? answered : true) && (
        <button type="button" style={ec.quiet} onClick={onImpressions}>
          Help refine {clientName ? `${clientName}'s` : "this"} profile and improve the app
        </button>
      )}
    </div>
  );
}

const ec = {
  wrap: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "var(--space-5)",
    padding: "var(--space-8)",
    textAlign: "center",
  },
  title: {
    fontSize: "var(--text-2xl)",
    fontWeight: "var(--weight-bold)",
    color: "var(--color-text-primary)",
    margin: 0,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-3)",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  primary: {
    font: "inherit",
    fontSize: "var(--text-base)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-text-on-accent)",
    background: "var(--color-accent)",
    border: "none",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-4) var(--space-6)",
    minHeight: 44,
    cursor: "pointer",
  },
  secondary: {
    font: "inherit",
    fontSize: "var(--text-base)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-secondary)",
    background: "none",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md)",
    padding: "var(--space-4) var(--space-6)",
    minHeight: 44,
    cursor: "pointer",
  },
  quiet: {
    font: "inherit",
    fontSize: "var(--text-sm)",
    color: "var(--color-text-tertiary)",
    background: "none",
    border: "none",
    cursor: "pointer",
    minHeight: 44,
    padding: "var(--space-2)",
    textDecoration: "underline",
  },
};

/**
 * Props are optional. As a ROUTE (/clients/:id/playthrough) everything comes
 * from the URL. EMBEDDED (Meet, section pages) it is handed clientId, an
 * optional sceneId, autoplay, and onExit — and renders no page chrome, so it
 * takes over the poster's frame rather than being a page of its own (C1).
 */
export default function PlayThroughPlayer({
  clientId: propClientId = null, sceneId: propSceneId = null,
  autoplay = false, embedded = false, onExit = null,
  // Preview of the coordinator's draft (the /preview/spec.json). From the
  // URL (?preview=true) on the standalone page; as a prop when the script
  // editor mounts the player over itself (Oct 11).
  preview = false,
  // Plan H: whether the end card offers Impressions for a single scene. The
  // Meet page says yes for Today's minute; a section row's play says no.
  // The full introduction always offers it; preview never does.
  offerImpressions = false,
  // A section play-through (Oct 20): its API base, and the name the counter
  // shows in place of "Introduction". Absent → the intro, exactly as before.
  playthroughBase: propBase = null,
  counterLabel = null,
} = {}) {
  // ── Feature flags + timing constants ─────────────────────────────────────
  // TODO: re-enable scene list when scene-navigation UI is ready
  const SHOW_SCENE_LIST      = false;
  // Duration of the full-bleed scene title card shown at the start of each
  // scene. The card replaces the image area, then images fade in.
  const SCENE_TITLE_MS       = 1500;
  // How long the card takes to dissolve once its time is up.
  const SCENE_TITLE_FADE_MS  = 400;
  // Extra beat after the card clears before the scene's images appear, so the
  // images don't arrive the instant the heading dissolves.
  const SCENE_IMAGE_DELAY_MS = 750;
  // Audio starts once the card has cleared and the images have arrived.
  const SCENE_START_DELAY_MS = SCENE_TITLE_MS + SCENE_TITLE_FADE_MS + SCENE_IMAGE_DELAY_MS;
  // Beat of silence after a scene's narration ends, before advancing to the
  // next scene. Lets the last image land instead of cutting away instantly.
  const SCENE_END_PAUSE_MS   = 1000;
  // ─────────────────────────────────────────────────────────────────────────

  const params                              = useParams();
  const clientId                            = propClientId || params.clientId;
  const [searchParams]                      = useSearchParams();
  const isPreviewMode                       = preview || (!embedded && searchParams.get("preview") === "true");
  const previewTab                          = useIsPreviewTab() && !embedded;
  useEffect(() => {
    if (!preview) return undefined;
    previewOverride = true;
    return () => { previewOverride = null; };
  }, [preview]);
  const introBase   = `/api/clients/${clientId}/playthrough`;
  const base        = propBase || introBase;
  // A section's play-through is not "the introduction": it never reports
  // PlaythroughView progress or completion, only scene_played per scene.
  const isSectionPlay = base !== introBase;
  // Set during render, not in an effect: the spec fetch below and the first
  // image URLs must already see it.
  const ownOverride = propBase ? base : null;
  baseOverride = ownOverride;
  // Clear only our own value: when one player replaces another, the new one
  // renders (and sets this) before the old one's cleanup runs.
  useEffect(() => () => { if (baseOverride === ownOverride) baseOverride = null; }, [ownOverride]);
  const navigate                            = useNavigate();
  const compact                             = useIsCompact();
  const portrait                            = useIsPortrait();
  const { user, share }                     = useAuth();
  // True once the stream has run to its end. Drives the end card, which is the
  // player's final state rather than something layered over it — see
  // StreamEndCard. Cleared by anything that moves off the end (replay, seek,
  // prev), so scrubbing back returns to the scene instead of needing the card
  // dismissed first.
  const [atEnd, setAtEnd] = useState(false);
  // Guards so progress/complete aren't re-sent on every render or on replay.
  const watchReportedRef = useRef({ maxProgress: 0, completed: false });

  // Fire-and-forget watch-event reporting. Never blocks or breaks playback — a
  // failed report is silently ignored (best-effort telemetry, not part of the
  // viewing experience). Skipped in preview: a coordinator previewing their own
  // build is not a DSP "watching".
  // Single-scene mode: ?scene=<scene_id> plays one scene of the finalized
  // stream on its own (Today's minute, a section play). Every mechanism
  // below works unchanged on a one-scene list. What differs: progress and
  // complete are NOT reported — PlaythroughView means "watched the
  // introduction" and one scene is not that — and scene_played is recorded
  // instead. An unknown id falls back to the full stream, never an error.
  const requestedScene = propSceneId || searchParams.get("scene") || null;

  function reportProgress(fraction) {
    if (isPreviewMode || !clientId || sceneMode || isSectionPlay) return;
    const f = Math.max(0, Math.min(1, fraction));
    if (f <= watchReportedRef.current.maxProgress) return; // monotonic client-side too
    watchReportedRef.current.maxProgress = f;
    fetch(`/api/clients/${clientId}/playthrough/progress`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ progress: f }),
    }).catch(() => {});
  }
  function reportScenePlayed(sceneId) {
    if (isPreviewMode || !clientId || !sceneId) return;
    fetch(`${base}/scene-played`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scene_id: sceneId }),
    }).catch(() => {});
  }
  function reportComplete() {
    if (isPreviewMode || !clientId) return;
    if (sceneMode) { reportScenePlayed(sceneMode); return; }
    if (isSectionPlay) return;   // each scene was reported as it finished (onAudioEnded)
    if (watchReportedRef.current.completed) return;
    watchReportedRef.current.completed = true;
    fetch(`/api/clients/${clientId}/playthrough/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }).catch(() => {});
  }

  const [statusData, setStatusData]         = useState(null);
  const [clientInfo, setClientInfo]         = useState({ name: "", pronouns: "", caps: null });
  // Embedded autoplay can be refused (iOS after the async spec fetch). When it
  // is, one large play button over the first frame is the whole UI.
  const [needsTap, setNeedsTap]             = useState(false);
  // Why audio is not playing, when the browser told us. Shown in the frame
  // so a stall is never silent (a caregiver's report of "nothing plays" has
  // no console attached).
  const [audioTrouble, setAudioTrouble]     = useState(null);
  // Stage (Plan G): progress within the scene for the dots, the lower-third
  // title's own timer, and the person a people beat is showing.
  const [sceneTimeMs, setSceneTimeMs]       = useState(0);
  const [titleOn, setTitleOn]               = useState(false);
  // What the person asked for, as opposed to what the <audio> element is
  // doing this instant: between scenes the element is paused for a beat, and
  // the controls must not pop back or flip to "Play" for it.
  const [userPaused, setUserPaused]         = useState(false);
  const LOWER_THIRD_MS                      = 4000;
  const [spec, setSpec]                     = useState(null);
  const [loadingSpec, setLoadingSpec]       = useState(true);
  const [error, setError]                   = useState(null);
  const [currentIdx, setCurrentIdx]         = useState(0);
  const [isPlaying, setIsPlaying]           = useState(false);
  const [hasStarted, setHasStarted]         = useState(false);
  // True while the scene's title card is on screen (start of each scene).
  const [showTitleCard, setShowTitleCard]   = useState(false);
  // True once the card's hold is over and it's dissolving.
  const [titleLeaving, setTitleLeaving]     = useState(false);
  // Gates the scene's images so they never flash beneath/behind the card.
  const [showSceneImages, setShowSceneImages] = useState(true);
  const audioRef                            = useRef(null);
  // Leaving the player (the Meet page swaps in a fresh one per scene, keyed
  // on the scene id) must silence this one: a detached <audio> can keep
  // playing in some browsers, which is how a row tap layered two narrations.
  useEffect(() => () => {
    const el = audioRef.current;
    if (!el) return;
    try { el.pause(); el.removeAttribute("src"); el.load(); } catch { /* fine */ }
  }, []);

  const [previewMap, setPreviewMap]                   = useState({});
  const [scenePlacementMap, setScenePlacementMap]     = useState({});
  const [heroPhotoUrl, setHeroPhotoUrl]               = useState(null);

  // Load spec
  useEffect(() => {
    fetch(base)
      .then(r => r.json())
      .then(data => {
        setStatusData(data);
        const specUrl = isPreviewMode
          ? (data.preview_available ? `${base}/preview/spec.json` : null)
          : data.spec_url;
        if (specUrl) {
          return fetch(specUrl)
            .then(r => { if (!r.ok) throw new Error(`Failed to load spec: ${r.status}`); return r.json(); })
            .then(specData => setSpec(specData));
        } else if (isPreviewMode) {
          throw new Error("No preview available yet.");
        } else {
          throw new Error(
            data.status === "generating" ? "Stream is still generating."
            : data.status === "failed"   ? `Generation failed: ${data.error || "unknown error"}`
            : "No stream has been generated yet."
          );
        }
      })
      .catch(e => setError(e.message))
      .finally(() => setLoadingSpec(false));
  }, [clientId]);

  // Load preview content (v1/v2 display data from CG script endpoint)
  useEffect(() => {
    fetch(`/api/clients/${clientId}/playthrough/script`)
      .then(r => r.json())
      .then(data => {
        const map = {};
        (data.scenes || []).forEach(s => { map[s.scene_id] = s.preview; });
        setPreviewMap(map);
      })
      .catch(() => {});
  }, [clientId]);

  // Load hero photo + sign_off/hero scene placements (PE doesn't bundle these)
  useEffect(() => {
    fetch(`/api/clients/${clientId}`)
      .then(r => r.json())
      .then(data => {
        if (data.hero_photo_media_id)
          setHeroPhotoUrl(`/api/clients/${clientId}/media/${data.hero_photo_media_id}?v=display`);
        // The finalized spec does not carry the person's name or pronouns;
        // the end card needs both ("That's a bit about Maris." / "about her?").
        setClientInfo({ name: data.display_name || "", pronouns: data.pronouns || "",
                        caps: data.viewer_capabilities || null });
      })
      .catch(() => {});

    const get = (sceneId) =>
      fetch(`/api/clients/${clientId}/placements?type=scene_image&target=${sceneId}`)
        .then(r => r.json()).then(d => Array.isArray(d) ? d : []).catch(() => []);
    Promise.all([get("hero"), get("sign_off")]).then(([h, s]) =>
      setScenePlacementMap({ hero: h, sign_off: s })
    );
  }, [clientId]);

  // Embedded autoplay: the poster tap was the gesture; the spec fetch since
  // then may or may not have lost it. Try once as soon as there is a scene.
  const autoplayedRef = useRef(false);
  useEffect(() => {
    // Runs again when the spinner gives way to the card (loadingSpec flips),
    // which is when the <audio> element first exists.
    if (!autoplay || autoplayedRef.current || !spec || loadingSpec || !audioRef.current) return;
    autoplayedRef.current = true;
    play();
  }, [autoplay, spec, loadingSpec]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Look ahead (Oct 12): fetch and decode this scene's pictures and the next
  // scene's as soon as a scene starts (and the first scene's the moment the
  // spec arrives, while the poster is still up), so no image change ever
  // waits on the network. Uses allScenes directly: it is derived below, but
  // the spec is the source and is stable here.
  useEffect(() => {
    const list = spec?.scenes || [];
    const req  = propSceneId || searchParams.get("scene");
    const inScope = req ? list.filter(sc => sc.scene_id === req) : list;
    const here = inScope[currentIdx]; const next = inScope[currentIdx + 1];
    preloadSceneImages(here, clientId);
    preloadSceneImages(next, clientId);
    (here?.people_visual || []).forEach(pv => preloadImage(normalizeMediaUrl(pv.source_url)));
  }, [spec, currentIdx, clientId, propSceneId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Stage hooks (Plan G) — declared here, before any early return.
  const prefersReducedStage = usePrefersReducedMotion();
  const running  = hasStarted && !userPaused && !atEnd;
  const controls = useAutoHide(running, 3000, prefersReducedStage || !running);
  useImmersive(true);
  // The lower-third title: on at every scene start once playing, off after a beat.
  useEffect(() => {
    if (!hasStarted) { setTitleOn(false); return; }
    setTitleOn(true);
    const t = setTimeout(() => setTitleOn(false), LOWER_THIRD_MS);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, hasStarted]);
  useEffect(() => { setSceneTimeMs(0); }, [currentIdx]);
  // Esc closes, ←/→ move a scene (desktop).
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") { if (onExit) onExit(); else navigate(isPreviewMode ? `/coordinator/clients/${clientId}/edit` : `/clients/${clientId}`); }
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const allScenes    = spec?.scenes || [];
  const sceneMode    = requestedScene && allScenes.some(s => s.scene_id === requestedScene)
                       ? requestedScene : null;
  const scenes       = sceneMode ? allScenes.filter(s => s.scene_id === sceneMode) : allScenes;
  // outputId retained as provenance label only — not used for URL resolution
  const outputId     = statusData?.output_id;
  // One scene on its own reports its own length, not the whole introduction's.
  const totalMs      = sceneMode
    ? scenes.reduce((a, sc) => a + (sc.duration_ms || 0), 0)
    : (spec?.total_duration_ms || 0);
  const currentScene = scenes[currentIdx];

  // Load audio when scene changes. Handles v2/v3 list and v1 single-object shapes.
  useEffect(() => {
    if (!currentScene || !audioRef.current) return;
    const audioPath = getAudioPath(currentScene);
    if (audioPath) {
      audioRef.current.src = isPreviewMode
        ? `${base}/preview/${audioPath}`
        : assetUrl(clientId, audioPath);
      // NOTE: deliberately no immediate play() here. Playback for a new scene is
      // started by the callers (play/next/seek/onAudioEnded) after
      // SCENE_START_DELAY_MS, so narration begins as the title card clears.
      // Calling play() here would start audio underneath the card.
    } else {
      // No audio for this scene (valid: e.g. a silent v3 scene with absent narration_text)
      audioRef.current.removeAttribute("src");
    }
  // loadingSpec: the spec can land one render before the spinner gives way
  // to the card, i.e. before <audio> exists. Re-run once it does.
  }, [currentIdx, clientId, spec, loadingSpec]);

  // Title card: show at the start of every scene once playback has begun.
  // Sequence per scene:
  //   0                          → card opaque (images hidden underneath)
  //   SCENE_TITLE_MS             → card starts fading out
  //   +SCENE_TITLE_FADE_MS       → card unmounts
  //   +SCENE_IMAGE_DELAY_MS      → images revealed; audio starts
  useEffect(() => {
    if (!hasStarted || !currentScene) {
      setShowTitleCard(false);
      setTitleLeaving(false);
      setShowSceneImages(true);   // no card in play → nothing to hide behind
      return;
    }
    setShowTitleCard(true);
    setTitleLeaving(false);
    setShowSceneImages(false);

    const tFade  = setTimeout(() => setTitleLeaving(true), SCENE_TITLE_MS);
    const tHide  = setTimeout(() => setShowTitleCard(false), SCENE_TITLE_MS + SCENE_TITLE_FADE_MS);
    const tImgs  = setTimeout(() => setShowSceneImages(true), SCENE_START_DELAY_MS);
    return () => { clearTimeout(tFade); clearTimeout(tHide); clearTimeout(tImgs); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, hasStarted]);

  // A real play() has been asked for since the last bless. The bless's
  // deferred pause() must not fire after that, or it aborts the real play.
  const realPlayRequestedRef = useRef(false);

  function whenCanPlay(el, maxMs) {
    // Resolve as soon as the element has enough buffered to start — or after
    // maxMs regardless, so a slow network degrades to "starts a bit late"
    // rather than "never starts".
    return new Promise(resolve => {
      if (!el || el.readyState >= 3) { resolve(); return; }
      let done = false;
      const finish = () => { if (done) return; done = true; el.removeEventListener("canplay", finish); resolve(); };
      el.addEventListener("canplay", finish, { once: true });
      setTimeout(finish, maxMs);
    });
  }

  function startAudio(attempt = 0) {
    const el = audioRef.current;
    if (!el) return;
    realPlayRequestedRef.current = true;
    // Only the first try waits for buffering; a retry goes straight in.
    (attempt === 0 ? whenCanPlay(el, 4000) : Promise.resolve()).then(() => {
      const p = audioRef.current?.play();
      if (!p || typeof p.catch !== "function") return;
      p.catch(err => {
        const name = err?.name || "";
        if (name === "AbortError" && attempt < 1) {
          // Our own bless pause (or a src swap) interrupted it — once more.
          setTimeout(() => startAudio(attempt + 1), 150);
          return;
        }
        console.warn("[playthrough] play() rejected:", name, err?.message, "src:", audioRef.current?.currentSrc || audioRef.current?.src);
        if (name === "NotAllowedError") {
          // Autoplay refused: no gesture reached the element. Offer the one
          // thing that will work — a tap, on a button, in the frame. On the
          // stage too: iPhone Chrome sat silent with nothing on screen until
          // a tap happened to reveal the controls (David, Oct 15).
          setIsPlaying(false); setNeedsTap(true);
          return;
        }
        setIsPlaying(false);
        setAudioTrouble(`Audio couldn't start (${name || "unknown"}).`);
      });
    });
  }

  function play()  {
    // iPhone Chrome blocks the deferred audioRef.play() below because it fires
    // from a setTimeout (to let the scene title card appear first), OUTSIDE the
    // tap gesture — so the first Play did nothing and it took Play/Pause/Play,
    // or Next-then-Play, to start. iOS unlocks audio per-element within a
    // gesture, so we "bless" this element SYNCHRONOUSLY here, inside the tap:
    // a muted micro play/pause on the element it will actually use. The deferred
    // real play() a moment later is then allowed.
    //
    // The bless's pause() is deferred until its promise resolves — and when
    // the src was set a moment ago (embedded autoplay) that can be AFTER the
    // real play() was issued, in which case the pause aborted it and the
    // player stalled on "Tap to play" (Plan E1). So: once a real play has been
    // requested, the bless leaves the element playing and only restores volume.
    const el = audioRef.current;
    realPlayRequestedRef.current = false;
    if (el) {
      try {
        const prevVol = el.volume;
        el.volume = 0;
        const p = el.play();
        const settle = () => {
          if (!realPlayRequestedRef.current) el.pause();
          el.volume = prevVol;
        };
        if (p && typeof p.then === "function") p.then(settle).catch(() => { el.volume = prevVol; });
        else settle();
      } catch { /* best-effort prime */ }
    }
    setHasStarted(true);
    setIsPlaying(true);
    setUserPaused(false);
    setNeedsTap(false);
    setTimeout(() => startAudio(), SCENE_START_DELAY_MS);
  }
  function pause() { setIsPlaying(false); setUserPaused(true); audioRef.current?.pause(); }
  function prev()  {
    if (currentIdx > 0) {
      setAtEnd(false);
      setCurrentIdx(i => i - 1);
      if (hasStarted && !userPaused) setTimeout(() => audioRef.current?.play().catch(() => {}), SCENE_START_DELAY_MS);
    }
  }
  function next()  {
    if (currentIdx < scenes.length - 1) {
      const nextIdx = currentIdx + 1;
      setCurrentIdx(nextIdx);
      if (scenes.length > 0) reportProgress((nextIdx + 1) / scenes.length);
      if (hasStarted && !userPaused) setTimeout(() => audioRef.current?.play().catch(() => {}), SCENE_START_DELAY_MS);
    }
    else setIsPlaying(false);
  }
  function onAudioEnded() {
    // A section play-through records each scene as it finishes (scene_played,
    // with its section) — never PlaythroughView, which means the introduction.
    if (isSectionPlay && !sceneMode) reportScenePlayed(scenes[currentIdx]?.scene_id);
    if (currentIdx < scenes.length - 1) {
      // Hold on the finished scene briefly, then advance. The next scene's
      // title card appears on advance, and its audio starts as the card clears.
      const nextIdx = currentIdx + 1;
      if (scenes.length > 0) reportProgress((nextIdx + 1) / scenes.length);
      setTimeout(() => {
        setCurrentIdx(i => i + 1);
        setTimeout(() => audioRef.current?.play().catch(() => {}), SCENE_START_DELAY_MS);
      }, SCENE_END_PAUSE_MS);
    } else {
      // Reached the end of the stream. The end card is the final state for
      // EVERYONE now, preview included — a coordinator who watches their own
      // build to the end should also land somewhere rather than on a frozen
      // last frame. What differs by audience is where "forward" goes and
      // whether Impressions is offered at all, both decided at the render site.
      setIsPlaying(false);
      reportComplete();
      setAtEnd(true);
    }
  }
  function seek(i) {
    // Any deliberate move off the end returns to the scene. The end card is a
    // state of the player, not a dialog standing in front of it.
    setAtEnd(false);
    setCurrentIdx(i);
    if (hasStarted && !userPaused) setTimeout(() => audioRef.current?.play().catch(() => {}), SCENE_START_DELAY_MS);
  }

  // "Play again" — back to the first scene and playing, in one action. seek(0)
  // alone would not do it: seek only resumes audio when already playing, and
  // reaching the end sets isPlaying false.
  function replay() {
    setAtEnd(false);
    setCurrentIdx(0);
    setIsPlaying(true);
    setUserPaused(false);
    setTimeout(() => audioRef.current?.play().catch(() => {}), SCENE_START_DELAY_MS);
  }

  if (loadingSpec) return <LoadingSpinner message="Loading stream…" />;

  // §6 stale-reference: status=ready but CG never fetched the output (pre-migration).
  // Show an actionable message instead of a broken player.
  if (statusData?.status === "ready" && statusData?.cg_output_available === false) return (
    <div style={styles.page}>
      <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:"100dvh", gap:"var(--space-4)", padding:"var(--space-8)", textAlign:"center" }}>
        <p style={{ fontSize:48 }}>⏳</p>
        <p style={{ fontSize:"var(--text-lg)", fontWeight:"var(--weight-semibold)", color:"var(--color-text-primary)" }}>Stream has expired</p>
        <p style={{ fontSize:"var(--text-sm)", color:"var(--color-text-secondary)", maxWidth:380, lineHeight:"var(--leading-normal)" }}>This stream was generated before CG took ownership of output files. Open the editor hub and re-finalize to get a permanent copy.</p>
      </div>
    </div>
  );
  if (error) return (
    <div style={styles.page}>
      <div style={styles.content}>
        {isPreviewMode && (
          <div style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.3)",
            borderRadius: "var(--radius-md)", padding: "8px 16px", fontSize: 12, fontWeight: 600,
            color: "#065f46", textAlign: "center" }}>
            Preview mode — {statusData?.preview_scene_ids?.join(", ") || "scene"} only. Not the live stream.
          </div>
        )}
        <ClientBreadcrumb clientName={spec?.client?.display_name || ""} role={user?.role} clientId={clientId} />
        <ErrorBanner message={error} />
      </div>
    </div>
  );

  // [2a] Fixed warnings from composition provenance block
  const fixedWarnings = spec?.composition?.warnings?.filter(w => w.policy === "fixed") ?? [];

  // Embedded: no page, no breadcrumb, no warnings banner — just the card, in
  // whatever frame the host page gives it, with an × to leave.
  //
  // This is a plain element, NOT a component defined here. A component
  // created inside render is a new type every render, so React unmounted and
  // remounted the whole card — the <audio> included — on every state change.
  // The first Play then talked to a fresh, src-less element and waited on it
  // forever: title card, first image, silence (Sep 30).
  // Player card — viewport-aware height (compact reservation differs).
  // ── The stage (Plan G) ────────────────────────────────────────────────────
  // The picture is the screen: fixed to the viewport, scrims and words over
  // it, controls that come and go. On a phone held sideways the image covers
  // the frame (cropped toward the face); held upright, and on a desktop, every
  // picture is shown whole on a blurred, darkened blow-up of itself — a
  // landscape photo cropped to a portrait sliver lost most of what it was
  // chosen for (David, Oct 15). Everything above this comment — state, audio,
  // effects — is the same player; this is its skin. Components are
  // module-level (E2 rule).
  const cover = compact && !portrait;
  const stageTitle = currentScene ? ((currentScene.title || "").trim() || humanizeSceneId(currentScene.scene_id)) : "";
  const words = { eyebrow: "", title: stageTitle, caption: currentScene?.on_screen_text || "" };
  const sceneMs = currentScene?.duration_ms || 0;
  const progress = sceneMs ? Math.max(0, Math.min(1, sceneTimeMs / sceneMs)) : 0;
  const counter = sceneMode
    ? stageTitle
    : `${counterLabel || "Introduction"} · ${Math.min(currentIdx + 1, scenes.length)} of ${scenes.length}`;
  const timeText = sceneMs ? `${mmssStage(sceneTimeMs)} / ${mmssStage(sceneMs)}` : "";
  const exit = () => {
    if (onExit) return onExit();
    // A coordinator's disposable preview tab (?preview=…): leaving means
    // closing the tab, back to the editor in the other tab. window.close()
    // is allowed for a tab a link opened and that has not navigated since;
    // if the browser refuses, fall through to the in-app route.
    if (previewTab) {
      try { window.close(); } catch { /* fall through */ }
      setTimeout(() => navigate(isPreviewMode ? `/coordinator/clients/${clientId}/edit` : `/clients/${clientId}`), 150);
      return;
    }
    if (isPreviewMode) return navigate(`/coordinator/clients/${clientId}/edit`);
    navigate(`/clients/${clientId}`);
  };
  const prevScene = currentIdx > 0 ? scenes[currentIdx - 1] : null;
  const nextScene = currentIdx < scenes.length - 1 ? scenes[currentIdx + 1] : null;
  const labelOf = sc => sc ? ((sc.title || "").trim() || humanizeSceneId(sc.scene_id)) : "";

  return (
    <div style={{ ...styles.stage, ...(prefersReducedStage ? {} : styles.stageFadeIn) }} data-testid="stage"
         onClick={() => { if (hasStarted) controls.toggle(); }}>
      <div style={styles.stagePicture}>
        {/* The last scene stays on the stage at the end (Oct 17): its final
            frame holds, and the end veil blurs it rather than cutting to black. */}
        {currentScene ? (
          <SceneContent
            key={currentScene.scene_id}
            scene={currentScene}
            clientId={clientId}
            preview={previewMap[currentScene.scene_id]}
            scenePlacementMap={scenePlacementMap}
            heroPhotoUrl={heroPhotoUrl}
            hasStarted={hasStarted}
            audioRef={audioRef}
            stage
            cover={cover}
          />
        ) : <p style={{ color: "var(--overlay-text-muted)", padding: 24 }}>No scenes</p>}
      </div>

      {!atEnd && currentScene && (
        <StageWords title={words.title} titleVisible={titleOn || !hasStarted}
                    eyebrow={words.eyebrow} caption={words.caption}
                    compact={compact} liftForControls={controls.visible && !sceneMode} />
      )}

      {!atEnd && (
        <StageControls
          counter={counter} playing={running} onPlay={play} onPause={pause} onClose={exit}
          scenes={scenes} currentIdx={currentIdx} onSeek={seek} progress={progress} timeText={timeText}
          prevLabel={labelOf(prevScene)} nextLabel={labelOf(nextScene)}
          onPrev={prevScene ? prev : null} onNext={nextScene ? next : null}
          single={!!sceneMode} visible={controls.visible || !hasStarted} onPoke={controls.poke} compact={compact}
        />
      )}

      {isPreviewMode && (
        <div style={styles.previewRibbon}>Preview — {statusData?.preview_scene_ids?.join(", ") || "scene"} only. Not the live stream.</div>
      )}

      {audioTrouble && !needsTap && !atEnd && (
        <div role="status" style={styles.audioTrouble} onClick={e => e.stopPropagation()}>{audioTrouble} <button type="button" style={styles.audioTroubleBtn} onClick={() => { setAudioTrouble(null); play(); }}>Try again</button></div>
      )}
      {needsTap && !atEnd && (
        <button type="button" onClick={e => { e.stopPropagation(); play(); }} aria-label="Tap to play" style={styles.tapToPlay}>
          <span style={styles.tapToPlayBtn}>▶</span>
          <span style={styles.tapToPlayLabel}>Tap to play</span>
        </button>
      )}

      {atEnd && (
        <div style={{ ...styles.endSheetWrap, animation: endTiming(prefersReducedStage).veil }} onClick={e => e.stopPropagation()}>
          <div style={{ ...styles.endSheet, animation: endTiming(prefersReducedStage).card }}>
            <StreamEndCard
              clientName={clientInfo.name || spec?.client?.display_name || ""}
              pronouns={clientInfo.pronouns}
              onReplay={replay}
              forward={isPreviewMode
                ? { label: "Back to the editor", onClick: exit }
                : sceneMode
                ? { label: clientInfo.name ? `Back to ${clientInfo.name}` : "Back", onClick: exit }
                : { label: "Continue", onClick: exit }}
              afterRef={sceneMode || (isSectionPlay ? `section:${counterLabel || "section"}`.slice(0, 64) : "full")}
              // A contributor adds through interviews, documents and photos —
              // no reflection or impressions here (Oct 24). The server refuses
              // them too; this just doesn't offer them.
              clientId={contributesWithoutEditing(clientInfo.caps) ? null
                        : reflectionClientId({ isPreviewMode, user, share, clientId })}
              reducedMotion={prefersReducedStage}
              // Nov 1: no "Help refine…" on the end card — one line, then out.
              // Impressions is reached when the coordinator asks for it.
              onImpressions={null}
            />
          </div>
        </div>
      )}

      <audio
        ref={audioRef}
        onEnded={onAudioEnded}
        onPlay={() => { setIsPlaying(true); setAudioTrouble(null); }}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={e => setSceneTimeMs(Math.round((e.currentTarget.currentTime || 0) * 1000))}
        onError={e => {
          const err = e.currentTarget?.error;
          const code = err?.code;
          const why = { 1: "aborted", 2: "network", 3: "decode", 4: "source not supported" }[code] || "unknown";
          console.warn("[playthrough] audio error:", code, why, err?.message, "src:", e.currentTarget?.currentSrc || e.currentTarget?.src);
          // No src for a silent scene is not trouble.
          if (e.currentTarget?.getAttribute("src")) setAudioTrouble(`Audio couldn't load (${why}).`);
        }}
        style={{ display: "none" }}
      />
    </div>
  );
}

function mmssStage(ms) {
  const t = Math.max(0, Math.round((ms || 0) / 1000));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  // ── Stage (Plan G) ──
  stage:        { position: "fixed", inset: 0, zIndex: 1000, background: "var(--overlay-ground)", overflow: "hidden", color: "var(--overlay-text)", WebkitTapHighlightColor: "transparent" },
  stageFadeIn:  { animation: "cg-fade-in 200ms ease both" },
  stagePicture: { position: "absolute", inset: 0, display: "flex", flexDirection: "column", zIndex: 1 },
  previewRibbon:{ position: "absolute", left: 0, right: 0, top: 0, zIndex: 7, background: "rgba(16,185,129,0.85)", color: "#fff", fontSize: 12, fontWeight: 600, textAlign: "center", padding: "4px 12px" },
  endSheetWrap: { position: "absolute", inset: 0, zIndex: 8, background: "rgba(15,14,12,0.5)",
                  backdropFilter: "blur(18px) saturate(0.9)", WebkitBackdropFilter: "blur(18px) saturate(0.9)",
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
  endSheet:     { background: "var(--color-surface)", color: "var(--color-text-primary)", borderRadius: "var(--radius-xl)", width: "min(560px, 100%)", maxHeight: "92%", overflowY: "auto", boxShadow: "var(--shadow-lg)" },
  page:     { minHeight: "100dvh", background: "var(--color-bg-base)", padding: "var(--space-6)" },
  embeddedWrap: { width: "100%", position: "relative" },
  playerCardEmbedded: { maxWidth: "none", height: "auto", aspectRatio: "16 / 10", minHeight: 320, position: "relative" },
  embeddedClose: { position: "absolute", top: 8, right: 8, zIndex: 20, width: 32, height: 32, borderRadius: 999, border: "none", background: "rgba(26,25,21,0.55)", color: "#fff", fontSize: 20, lineHeight: 1, cursor: "pointer" },
  audioTrouble: { position: "absolute", left: 12, right: 12, bottom: 12, zIndex: 14, background: "rgba(26,25,21,0.8)", color: "#fff", fontSize: 13, padding: "8px 12px", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 },
  audioTroubleBtn: { font: "inherit", fontSize: 13, background: "none", border: "1px solid rgba(255,255,255,0.6)", color: "#fff", borderRadius: 6, padding: "2px 10px", cursor: "pointer" },
  tapToPlay: { position: "absolute", inset: 0, zIndex: 15, background: "rgba(26,25,21,0.35)", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, color: "#fff", font: "inherit" },
  tapToPlayBtn: { width: 72, height: 72, borderRadius: 999, background: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, paddingLeft: 4 },
  tapToPlayLabel: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)" },
  pageCompact: { padding: "var(--space-3)" },
  content:  { maxWidth: 800, margin: "0 auto", display: "flex", flexDirection: "column", gap: "var(--space-6)" },
  contentCompact: { gap: "var(--space-4)" },
  back:     { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)", textDecoration: "none", display: "inline-block" },

  playerCard: {
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-xl)",
    overflow: "hidden",
    boxShadow: "var(--shadow-md)",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: 800,
    // Viewport-AWARE height, capped — not a fixed 800px. The fixed height
    // overflowed short viewports (an iPhone is often < 800px tall), pushing the
    // caption and controls below the fold while the scene area ate the screen.
    // min() keeps the comfortable 800px ceiling on large screens (the cap wins)
    // and shrinks to fit on phones (the viewport term wins), so the controls —
    // pinned at the card's bottom via flexShrink:0 — stay visible everywhere.
    // 100dvh is the dynamic viewport unit (accounts for mobile browser chrome);
    // the ~160px reservation covers page padding, the breadcrumb, and the
    // content gap above the card. The Ken Burns geometry measures its container
    // at runtime (getBoundingClientRect), so it adapts to the dynamic height
    // automatically — no hardcoded-800 dependency to break.
    height: "min(800px, calc(100dvh - 160px))",
    // Floor so the card never collapses to nothing on very short viewports.
    minHeight: 420,
  },
  // Compact reservation is smaller: compact page padding (var(--space-3) vs -6)
  // and content gap (var(--space-4) vs -6) free ~32px of vertical space, so the
  // scene box gets more height on a phone while controls still clear the fold.
  // Lower floor too — small phones are short.
  playerCardCompact: {
    height: "min(800px, calc(100dvh - 124px))",
    minHeight: 360,
  },

  sceneArea: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    overflow: "hidden",
    background: "var(--color-bg-subtle)",
    position: "relative",   // positioning context for the scene title card overlay
  },

  controls:       { display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-5) var(--space-6)", borderTop: "1px solid var(--color-border)", flexShrink: 0 },
  controlsCompact: { padding: "var(--space-3) var(--space-4)" },
  controlBtn:     { width: "var(--tap-target-min)", height: "var(--tap-target-min)", borderRadius: "var(--radius-full)", border: "1px solid var(--color-border)", background: "none", cursor: "pointer", fontSize: "var(--text-xl)", color: "var(--color-text-secondary)", display: "flex", alignItems: "center", justifyContent: "center" },
  playBtn:        { width: 48, height: 48, borderRadius: "var(--radius-full)", background: "var(--color-accent)", color: "#fff", border: "none", cursor: "pointer", fontSize: "var(--text-base)", fontWeight: "var(--weight-bold)", display: "flex", alignItems: "center", justifyContent: "center" },
  duration:       { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  sceneList:      { display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  sceneListItem:  { display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-3) var(--space-4)", background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", cursor: "pointer", textAlign: "left" },
  sceneListItemActive: { background: "var(--color-accent-subtle)", border: "1px solid var(--color-accent)" },
  sceneListLabel: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", width: 120, flexShrink: 0 },
  sceneListTitle: { fontSize: "var(--text-sm)", color: "var(--color-text-primary)", flex: 1 },
  sceneListDur:   { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
};

const sceneStyles = {
  wrap: {
    flex: 1,
    minHeight: 0,
    // minWidth:0 — sceneArea (this div's parent) is a ROW-direction flex
    // container with no flexDirection override; this div is its row-axis
    // flex item. Row main-axis sizing defaults to min-width:auto, which lets
    // a flex item grow to its content's preferred width (here, the Ken
    // Burns-scaled image many levels down) even past its allocated flex
    // space. This is the actual root of the width-grows-during-zoom bug —
    // one level above where the previous fix (on fillContainerRef) landed,
    // which addressed a column-direction (cross-axis) relationship that
    // doesn't use the same min-width:auto floor at all.
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "var(--space-5) var(--space-6)",
    gap: "var(--space-3)",
    textAlign: "center",
  },
  // Captions: one consistent treatment across all scenes (incl. Meet/hero).
  // Sized up from text-sm so they hold their own beside large images and the
  // scene title cards. Caption heads were retired — the scene heading now
  // appears as the full-bleed title card at the start of each scene.
  heroTitle:    { fontSize: "var(--text-xl)", fontWeight: "var(--weight-regular)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: 0, textAlign: "center" },
  heroOneliner: { fontSize: "var(--text-base)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: 0 },
  pronouns:     { fontSize: "var(--text-base)", color: "var(--color-text-tertiary)", fontWeight: "var(--weight-regular)" },
  imageCaption: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", textAlign: "center", fontStyle: "italic" },
  signOffText:  { fontSize: "var(--text-xl)", color: "var(--color-text-primary)", lineHeight: "var(--leading-loose)", fontStyle: "italic", margin: 0 },
  // On the stage the wrap is the whole frame: no padding, no gap (Plan G).
  wrapStage: { padding: 0, gap: 0 },
  previewBlock: { display: "flex", flexDirection: "column", gap: "var(--space-2)", alignItems: "center" },
  previewBody:  { fontSize: "var(--text-xl)", color: "var(--color-text-secondary)", lineHeight: "var(--leading-normal)", margin: 0, textAlign: "center" },
  peopleRow:    { display: "flex", gap: "var(--space-5)", justifyContent: "center", flexWrap: "wrap" },
  personCard:   { display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--space-2)" },
  personAvatar: { width: 72, height: 72, borderRadius: "var(--radius-md)", background: "var(--color-accent-subtle)", color: "var(--color-accent)", fontSize: "var(--text-xl)", fontWeight: "var(--weight-bold)", display: "flex", alignItems: "center", justifyContent: "center" },
  personPhoto:  { width: 72, height: 72, borderRadius: "var(--radius-md)", objectFit: "contain", background: "var(--color-bg-inset)" },
  personName:   { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  personRel:    { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
};

const timelineStyles = {
  wrap:    { display: "flex", height: 4, gap: 2, padding: "0 var(--space-6)", background: "var(--color-bg-inset)", flexShrink: 0 },
  segment: { height: "100%", borderRadius: 2, cursor: "pointer", transition: "background var(--transition-fast)" },
};
